import React, { useEffect, useState, Component } from 'react';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Card } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { Modal } from '../components/ui/Modal';
import { Textarea } from '../components/ui/Textarea';
import { DatePicker } from '../components/ui/DatePicker';
import { Tooltip } from '../components/ui/Tooltip';
import { Pagination } from '../components/ui/Pagination';
import { ColumnCustomizer } from '../components/ColumnCustomizer';
import { CalendarView } from '../components/CalendarView';
import { useSearch } from '../hooks/useSearch';
import { useFilters } from '../hooks/useFilters';
import { usePagination } from '../hooks/usePagination';
import { useNotifications } from '../contexts/NotificationContext';
import { ProgressiveNumberInput } from '../components/ProgressiveNumberInput';
import { GenerateActivityWizardModal } from '../components/operations/GenerateActivityWizardModal';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Eye, Edit, Trash2, Plus, Save, CheckCircle2, AlertCircle, HardHat, Calendar, FileText, Receipt, GraduationCap, ClipboardList, AlertTriangle, TrendingUp, Wrench, Hammer, Mail, ExternalLink, X, Search, Filter, Download, List, CalendarIcon, LayoutGrid, Euro } from 'lucide-react';
// ─── Section Config ───────────────────────────────────────────────────────────
const sectionConfig: Record<string, {
  title: string;
  description: string;
  icon: React.ElementType;
  newLabel: string;
}> = {
  workers: {
    title: 'Lavoratori',
    description: 'Dipendenti, formazione e sorveglianza sanitaria.',
    icon: HardHat,
    newLabel: 'Nuovo lavoratore'
  },
  deadlines: {
    title: 'Scadenze',
    description: 'Scadenze tecniche e normative.',
    icon: Calendar,
    newLabel: 'Nuova scadenza'
  },
  contracts: {
    title: 'Offerte e contratti',
    description: 'Offerte, contratti attivi e storico.',
    icon: FileText,
    newLabel: 'Nuovo contratto'
  },
  invoices: {
    title: 'Fatturazione',
    description: 'Fatture, proforma e scadenziario.',
    icon: Receipt,
    newLabel: 'Nuova fattura'
  },
  training: {
    title: 'Formazione',
    description: 'Corsi, iscrizioni e certificazioni.',
    icon: GraduationCap,
    newLabel: 'Nuovo corso'
  },
  operations: {
    title: 'Attività operative',
    description: 'Pianificazione e reportistica delle attività.',
    icon: ClipboardList,
    newLabel: 'Nuova attività'
  },
  non_conformities: {
    title: 'Non conformità',
    description: 'NC, reclami e azioni correttive.',
    icon: AlertTriangle,
    newLabel: 'Nuova NC'
  },
  improvements: {
    title: 'Miglioramenti',
    description: 'Proposte e piani di miglioramento.',
    icon: TrendingUp,
    newLabel: 'Nuovo miglioramento'
  },
  equipment: {
    title: 'Macchinari',
    description: 'Asset aziendali e manutenzioni programmate.',
    icon: Wrench,
    newLabel: 'Nuovo macchinario'
  },
  maintenance: {
    title: 'Manutenzione',
    description: 'Storico interventi e manutenzioni.',
    icon: Hammer,
    newLabel: 'Nuova manutenzione'
  }
};
// ─── Columns Config ───────────────────────────────────────────────────────────
const columnsConfigMap: Record<string, {
  key: string;
  label: string;
}[]> = {
  workers: [{
    key: 'name',
    label: 'Lavoratore'
  }, {
    key: 'role',
    label: 'Mansione'
  }, {
    key: 'trainingStatus',
    label: 'Formazione'
  }, {
    key: 'healthStatus',
    label: 'Sorv. sanitaria'
  }, {
    key: 'nextExpiry',
    label: 'Prox. scadenza'
  }, {
    key: 'actions',
    label: 'Azioni'
  }],
  deadlines: [{
    key: 'code',
    label: 'Codice'
  }, {
    key: 'description',
    label: 'Descrizione'
  }, {
    key: 'type',
    label: 'Tipologia'
  }, {
    key: 'date',
    label: 'Data scadenza'
  }, {
    key: 'notifyClient',
    label: 'Notifica'
  }, {
    key: 'responsible',
    label: 'Responsabile'
  }, {
    key: 'status',
    label: 'Stato'
  }, {
    key: 'actions',
    label: 'Azioni'
  }],
  contracts: [{
    key: 'code',
    label: 'Codice'
  }, {
    key: 'object',
    label: 'Oggetto'
  }, {
    key: 'type',
    label: 'Tipo'
  }, {
    key: 'value',
    label: 'Valore'
  }, {
    key: 'status',
    label: 'Stato'
  }, {
    key: 'date',
    label: 'Data'
  }, {
    key: 'actions',
    label: 'Azioni'
  }],
  invoices: [{
    key: 'number',
    label: 'Numero'
  }, {
    key: 'description',
    label: 'Descrizione'
  }, {
    key: 'amount',
    label: 'Importo'
  }, {
    key: 'status',
    label: 'Stato'
  }, {
    key: 'dueDate',
    label: 'Scadenza'
  }, {
    key: 'actions',
    label: 'Azioni'
  }],
  training: [{
    key: 'code',
    label: 'Codice'
  }, {
    key: 'title',
    label: 'Titolo'
  }, {
    key: 'type',
    label: 'Tipologia'
  }, {
    key: 'mode',
    label: 'Modalità'
  }, {
    key: 'date',
    label: 'Data'
  }, {
    key: 'enrolled',
    label: 'Iscritti'
  }, {
    key: 'status',
    label: 'Stato'
  }, {
    key: 'actions',
    label: 'Azioni'
  }],
  operations: [{
    key: 'code',
    label: 'Codice'
  }, {
    key: 'activityType',
    label: 'Attività'
  }, {
    key: 'ambito',
    label: 'Ambito'
  }, {
    key: 'contract',
    label: 'Contratto'
  }, {
    key: 'operator',
    label: 'Operatore'
  }, {
    key: 'status',
    label: 'Stato'
  }, {
    key: 'deadline',
    label: 'Scadenza'
  }, {
    key: 'actions',
    label: 'Azioni'
  }],
  non_conformities: [{
    key: 'number',
    label: 'Codice'
  }, {
    key: 'type',
    label: 'Tipologia'
  }, {
    key: 'origin',
    label: 'Origine'
  }, {
    key: 'description',
    label: 'Descrizione'
  }, {
    key: 'date',
    label: 'Data rilev.'
  }, {
    key: 'responsible',
    label: 'Responsabile'
  }, {
    key: 'status',
    label: 'Stato'
  }, {
    key: 'actions',
    label: 'Azioni'
  }],
  improvements: [{
    key: 'number',
    label: 'Codice'
  }, {
    key: 'subject',
    label: 'Oggetto'
  }, {
    key: 'description',
    label: 'Descrizione'
  }, {
    key: 'date',
    label: 'Data proposta'
  }, {
    key: 'responsible',
    label: 'Responsabile'
  }, {
    key: 'status',
    label: 'Stato'
  }, {
    key: 'actions',
    label: 'Azioni'
  }],
  equipment: [{
    key: 'code',
    label: 'Codice'
  }, {
    key: 'name',
    label: 'Macchinario'
  }, {
    key: 'type',
    label: 'Tipologia'
  }, {
    key: 'lastMaintenance',
    label: 'Ultima manut.'
  }, {
    key: 'nextMaintenance',
    label: 'Prox. manut.'
  }, {
    key: 'responsible',
    label: 'Responsabile'
  }, {
    key: 'maintenanceType',
    label: 'Tipo manut.'
  }, {
    key: 'actions',
    label: 'Azioni'
  }],
  maintenance: [{
    key: 'code',
    label: 'Codice'
  }, {
    key: 'equipment',
    label: 'Macchinario'
  }, {
    key: 'type',
    label: 'Tipo'
  }, {
    key: 'date',
    label: 'Data esecuzione'
  }, {
    key: 'result',
    label: 'Esito'
  }, {
    key: 'nextDate',
    label: 'Prox. scadenza'
  }, {
    key: 'responsible',
    label: 'Responsabile'
  }, {
    key: 'actions',
    label: 'Azioni'
  }]
};
// ─── Mock Data ────────────────────────────────────────────────────────────────
const mockWorkers = [{
  id: 1,
  name: 'Mario Rossi',
  role: 'Operaio',
  trainingStatus: 'valid',
  healthStatus: 'fit',
  nextExpiry: '12/05/2025'
}, {
  id: 2,
  name: 'Luigi Verdi',
  role: 'Impiegato',
  trainingStatus: 'expiring',
  healthStatus: 'fit',
  nextExpiry: '10/06/2025'
}, {
  id: 3,
  name: 'Anna Bianchi',
  role: 'Tecnico',
  trainingStatus: 'expired',
  healthStatus: 'fit',
  nextExpiry: '15/01/2025'
}, {
  id: 4,
  name: 'Carlo Neri',
  role: 'Magazziniere',
  trainingStatus: 'valid',
  healthStatus: 'fit',
  nextExpiry: '20/08/2025'
}, {
  id: 5,
  name: 'Giulia Russo',
  role: 'Amministrativa',
  trainingStatus: 'valid',
  healthStatus: 'fit',
  nextExpiry: '05/09/2025'
}, {
  id: 6,
  name: 'Fabio Conti',
  role: 'Preposto',
  trainingStatus: 'expiring',
  healthStatus: 'fit',
  nextExpiry: '01/07/2025'
}, {
  id: 7,
  name: 'Sara Mancini',
  role: 'RLS',
  trainingStatus: 'valid',
  healthStatus: 'fit',
  nextExpiry: '18/10/2025'
}];
const mockDeadlines = [{
  id: 1,
  code: 'SCA-2025/001',
  description: 'Rinnovo DVR',
  type: 'Audit interno',
  date: '2025-06-15',
  notifyClient: 'Sì',
  responsible: 'Alessio',
  status: 'In scadenza'
}, {
  id: 2,
  code: 'SCA-2025/002',
  description: 'Verifica estintori',
  type: 'Prova di emergenza',
  date: '2025-06-20',
  notifyClient: 'No',
  responsible: 'Lorenzo',
  status: 'Pianificata'
}, {
  id: 3,
  code: 'SCA-2025/003',
  description: 'Tampone acque',
  type: 'Tampone alimentare',
  date: '2025-07-01',
  notifyClient: 'Sì',
  responsible: 'Gian Franco',
  status: 'Pianificata'
}, {
  id: 4,
  code: 'SCA-2025/004',
  description: 'Riunione sicurezza',
  type: 'Riunione annuale',
  date: '2025-05-30',
  notifyClient: 'Sì',
  responsible: 'Alessio',
  status: 'Urgente'
}, {
  id: 5,
  code: 'SCA-2025/005',
  description: 'Campionamento prodotti',
  type: 'Campionamento alimentare',
  date: '2025-08-10',
  notifyClient: 'No',
  responsible: 'Tiziano',
  status: 'Pianificata'
}, {
  id: 6,
  code: 'SCA-2025/006',
  description: 'Report annuale sicurezza',
  type: 'Report annuale',
  date: '2025-12-31',
  notifyClient: 'Sì',
  responsible: 'Alessio',
  status: 'Pianificata'
}, {
  id: 7,
  code: 'SCA-2025/007',
  description: 'Corso aggiornamento RLS',
  type: 'Audit interno',
  date: '2025-05-28',
  notifyClient: 'No',
  responsible: 'Lorenzo',
  status: 'Urgente'
}];
const mockContracts = [{
  id: 1,
  code: 'CTR-2025/001',
  object: 'Sicurezza Annuale',
  type: 'Contratto',
  value: '€ 3.200',
  status: 'Attivo',
  date: '01/01/2025'
}, {
  id: 2,
  code: 'CTR-2025/002',
  object: 'HACCP Base',
  type: 'Contratto',
  value: '€ 1.500',
  status: 'Attivo',
  date: '15/02/2025'
}, {
  id: 3,
  code: 'PREV-2025/001',
  object: 'Formazione Lavoratori',
  type: 'Offerta',
  value: '€ 2.800',
  status: 'In attesa',
  date: '10/05/2025'
}, {
  id: 4,
  code: 'CTR-2024/008',
  object: 'Privacy GDPR',
  type: 'Contratto',
  value: '€ 900',
  status: 'Scaduto',
  date: '01/01/2024'
}, {
  id: 5,
  code: 'PREV-2025/002',
  object: 'Audit Interno',
  type: 'Offerta',
  value: '€ 1.200',
  status: 'Bozza',
  date: '20/05/2025'
}, {
  id: 6,
  code: 'CTR-2025/003',
  object: 'Sorveglianza Sanitaria',
  type: 'Contratto',
  value: '€ 2.100',
  status: 'Attivo',
  date: '01/03/2025'
}, {
  id: 7,
  code: 'PREV-2025/003',
  object: 'Aggiornamento DVR',
  type: 'Offerta',
  value: '€ 750',
  status: 'In attesa',
  date: '12/05/2025'
}];
const mockInvoices = [{
  id: 1,
  number: 'FT-2025/045',
  description: 'Canone sicurezza Q1',
  amount: '€ 800',
  status: 'Pagata',
  dueDate: '15/04/2025'
}, {
  id: 2,
  number: 'FT-2025/052',
  description: 'Corso formazione HACCP',
  amount: '€ 1.200',
  status: 'In attesa',
  dueDate: '10/05/2025'
}, {
  id: 3,
  number: 'PRF-2025/012',
  description: 'Consulenza privacy',
  amount: '€ 450',
  status: 'Bozza',
  dueDate: '20/05/2025'
}, {
  id: 4,
  number: 'FT-2025/038',
  description: 'Sopralluogo periodico',
  amount: '€ 350',
  status: 'Pagata',
  dueDate: '01/04/2025'
}, {
  id: 5,
  number: 'FT-2025/061',
  description: 'Aggiornamento DVR',
  amount: '€ 750',
  status: 'Scaduta',
  dueDate: '30/04/2025'
}, {
  id: 6,
  number: 'FT-2025/067',
  description: 'Sorveglianza sanitaria',
  amount: '€ 520',
  status: 'In attesa',
  dueDate: '15/06/2025'
}, {
  id: 7,
  number: 'PRF-2025/015',
  description: 'Audit interno',
  amount: '€ 600',
  status: 'Bozza',
  dueDate: '30/06/2025'
}];
const mockTraining = [{
  id: 1,
  code: 'CRS-001',
  title: 'Sicurezza Generale Lavoratori',
  type: 'Sicurezza',
  mode: 'Sede SEF',
  date: '2025-05-20',
  enrolled: 5,
  status: 'In corso'
}, {
  id: 2,
  code: 'CRS-002',
  title: 'HACCP Responsabile',
  type: 'HACCP',
  mode: 'Sede cliente',
  date: '2025-05-22',
  enrolled: 3,
  status: 'In corso'
}, {
  id: 3,
  code: 'CRS-003',
  title: 'Antincendio Rischio Medio',
  type: 'Sicurezza',
  mode: 'Sede SEF',
  date: '2025-06-05',
  enrolled: 4,
  status: 'Programmato'
}, {
  id: 4,
  code: 'CRS-004',
  title: 'Primo Soccorso Gruppo B',
  type: 'Sicurezza',
  mode: 'A distanza',
  date: '2025-06-10',
  enrolled: 6,
  status: 'Programmato'
}, {
  id: 5,
  code: 'CRS-005',
  title: 'Aggiornamento RLS',
  type: 'Sicurezza',
  mode: 'Sede SEF',
  date: '2025-04-15',
  enrolled: 2,
  status: 'Completato'
}, {
  id: 6,
  code: 'CRS-006',
  title: 'Preposto Aggiornamento',
  type: 'Sicurezza',
  mode: 'Sede SEF',
  date: '2025-03-10',
  enrolled: 3,
  status: 'Completato'
}, {
  id: 7,
  code: 'CRS-007',
  title: 'Privacy GDPR Base',
  type: 'Privacy',
  mode: 'A distanza',
  date: '2025-07-01',
  enrolled: 0,
  status: 'Programmato'
}];
const mockOperations = [{
  id: 1,
  code: 'ATT-2025/001',
  activityType: 'Sopralluogo periodico',
  ambito: 'Sicurezza',
  contract: 'CTR-2025/001',
  operator: 'Alessio',
  zone: 'Arezzo',
  status: 'completo',
  deadline: '30/04/2025'
}, {
  id: 2,
  code: 'ATT-2025/002',
  activityType: 'Backoffice – Redazione documentazione',
  ambito: 'Privacy',
  contract: 'CTR-2025/002',
  operator: 'Lorenzo',
  zone: 'Arezzo',
  status: 'in_svolgimento_attivo',
  deadline: '15/06/2025'
}, {
  id: 3,
  code: 'ATT-2025/003',
  activityType: 'Sopralluogo iniziale e check list',
  ambito: 'HACCP',
  contract: 'CTR-2025/002',
  operator: 'Gian Franco',
  zone: 'Arezzo',
  status: 'calendarizzato',
  deadline: '20/06/2025'
}, {
  id: 4,
  code: 'ATT-2025/004',
  activityType: 'Campionamenti e tamponi alimentari',
  ambito: 'HACCP',
  contract: 'CTR-2025/002',
  operator: 'Alessio',
  zone: 'Arezzo',
  status: 'pianificato',
  deadline: '30/07/2025'
}, {
  id: 5,
  code: 'ATT-2025/005',
  activityType: 'Riunione in ufficio',
  ambito: 'Sicurezza',
  contract: 'CTR-2025/001',
  operator: 'Lorenzo',
  zone: 'Arezzo',
  status: 'da_pianificare',
  deadline: '31/08/2025'
}, {
  id: 6,
  code: 'ATT-2025/006',
  activityType: 'Consulenza via email',
  ambito: 'Ambiente',
  contract: 'CTR-2025/003',
  operator: 'Tiziano',
  zone: 'Arezzo',
  status: 'completo',
  deadline: '10/04/2025'
}, {
  id: 7,
  code: 'ATT-2025/007',
  activityType: 'Sopralluogo consegna',
  ambito: 'Sicurezza',
  contract: 'CTR-2025/001',
  operator: 'Alessio',
  zone: 'Arezzo',
  status: 'in_svolgimento_sospeso',
  deadline: '25/06/2025'
}];
const mockNonConformities = [{
  id: 1,
  number: 'NC-2025/001',
  type: 'Non conformità maggiore',
  origin: 'Interna',
  description: 'Mancanza DPI in reparto produzione',
  date: '2025-04-12',
  responsible: 'Lorenzo',
  status: 'Aperta'
}, {
  id: 2,
  number: 'NC-2025/002',
  type: 'Non conformità minore',
  origin: 'Cliente (reclamo)',
  description: 'Documentazione incompleta',
  date: '2025-05-05',
  responsible: 'Alessio',
  status: 'Chiusa'
}, {
  id: 3,
  number: 'NC-2025/003',
  type: 'Osservazione',
  origin: 'Interna',
  description: 'Segnaletica non aggiornata',
  date: '2025-05-15',
  responsible: 'Gian Franco',
  status: 'Aperta'
}, {
  id: 4,
  number: 'NC-2025/004',
  type: 'Non conformità minore',
  origin: 'Fornitore',
  description: 'Prodotto non conforme alle specifiche',
  date: '2025-03-20',
  responsible: 'Lorenzo',
  status: 'Chiusa'
}, {
  id: 5,
  number: 'NC-2025/005',
  type: 'Osservazione',
  origin: 'Interna',
  description: 'Procedura non rispettata',
  date: '2025-05-22',
  responsible: 'Alessio',
  status: 'Sospesa'
}, {
  id: 6,
  number: 'NC-2025/006',
  type: 'Non conformità maggiore',
  origin: 'Cliente (reclamo)',
  description: 'Ritardo nella consegna documentazione',
  date: '2025-04-30',
  responsible: 'Tiziano',
  status: 'Aperta'
}];
const mockImprovements = [{
  id: 1,
  number: 'MIG-2025/001',
  subject: 'Digitalizzazione archivio DVR',
  description: 'Proposta per archiviazione digitale di tutti i DVR',
  date: '2025-03-01',
  responsible: 'Alessio',
  status: 'Approvato'
}, {
  id: 2,
  number: 'MIG-2025/002',
  subject: 'Formazione extra sicurezza',
  description: 'Corso aggiuntivo per addetti al magazzino',
  date: '2025-04-15',
  responsible: 'Lorenzo',
  status: 'In valutazione'
}, {
  id: 3,
  number: 'MIG-2025/003',
  subject: 'Nuovi DPI ergonomici',
  description: 'Sostituzione DPI con modelli certificati ergonomici',
  date: '2025-01-10',
  responsible: 'Gian Franco',
  status: 'Approvato'
}, {
  id: 4,
  number: 'MIG-2025/004',
  subject: 'Aggiornamento piano emergenza',
  description: 'Revisione completa del piano di emergenza',
  date: '2025-05-01',
  responsible: 'Alessio',
  status: 'In valutazione'
}, {
  id: 5,
  number: 'MIG-2025/005',
  subject: 'Segnaletica bilingue',
  description: 'Aggiunta segnaletica in italiano e inglese',
  date: '2025-02-20',
  responsible: 'Tiziano',
  status: 'Sospesa'
}, {
  id: 6,
  number: 'MIG-2025/006',
  subject: 'Check-list digitale sopralluoghi',
  description: 'App mobile per compilazione check-list in loco',
  date: '2025-05-10',
  responsible: 'Lorenzo',
  status: 'In valutazione'
}];
const mockEquipment = [{
  id: 1,
  code: 'EQP-001',
  name: 'Fonometro Brüel & Kjær',
  type: 'Strumento Misura',
  lastMaintenance: '2025-01-10',
  nextMaintenance: '2026-01-10',
  responsible: 'Alessio',
  maintenanceType: 'Ordinaria'
}, {
  id: 2,
  code: 'EQP-002',
  name: 'Carrello elevatore Toyota',
  type: 'Veicolo',
  lastMaintenance: '2025-03-15',
  nextMaintenance: '2025-09-15',
  responsible: 'Lorenzo',
  maintenanceType: 'Ordinaria'
}, {
  id: 3,
  code: 'EQP-003',
  name: 'Pressa idraulica 50T',
  type: 'Attrezzatura',
  lastMaintenance: '2024-12-01',
  nextMaintenance: '2025-06-01',
  responsible: 'Gian Franco',
  maintenanceType: 'Straordinaria'
}, {
  id: 4,
  code: 'EQP-004',
  name: 'Notebook Dell Latitude',
  type: 'Hardware',
  lastMaintenance: '2025-02-20',
  nextMaintenance: '2026-02-20',
  responsible: 'Alessio',
  maintenanceType: 'Ordinaria'
}, {
  id: 5,
  code: 'EQP-005',
  name: 'Trapano a colonna',
  type: 'Attrezzatura',
  lastMaintenance: '2025-04-05',
  nextMaintenance: '2025-10-05',
  responsible: 'Lorenzo',
  maintenanceType: 'Ordinaria'
}, {
  id: 6,
  code: 'EQP-006',
  name: 'Misuratore CO2',
  type: 'Strumento Misura',
  lastMaintenance: '2025-01-25',
  nextMaintenance: '2026-01-25',
  responsible: 'Tiziano',
  maintenanceType: 'Ordinaria'
}, {
  id: 7,
  code: 'EQP-007',
  name: 'Autocarro Iveco Daily',
  type: 'Veicolo',
  lastMaintenance: '2025-04-01',
  nextMaintenance: '2025-10-01',
  responsible: 'Gian Franco',
  maintenanceType: 'Straordinaria'
}];
const mockMaintenance = [{
  id: 1,
  code: 'MNT-2025/001',
  equipment: 'Fonometro Brüel & Kjær',
  type: 'Ordinaria',
  date: '2025-01-10',
  result: 'Positivo',
  nextDate: '2026-01-10',
  responsible: 'Alessio'
}, {
  id: 2,
  code: 'MNT-2025/002',
  equipment: 'Carrello elevatore Toyota',
  type: 'Ordinaria',
  date: '2025-03-15',
  result: 'Positivo',
  nextDate: '2025-09-15',
  responsible: 'Lorenzo'
}, {
  id: 3,
  code: 'MNT-2025/003',
  equipment: 'Pressa idraulica 50T',
  type: 'Straordinaria',
  date: '2024-12-01',
  result: 'Negativo',
  nextDate: '2025-06-01',
  responsible: 'Gian Franco'
}, {
  id: 4,
  code: 'MNT-2025/004',
  equipment: 'Notebook Dell Latitude',
  type: 'Ordinaria',
  date: '2025-02-20',
  result: 'Positivo',
  nextDate: '2026-02-20',
  responsible: 'Alessio'
}, {
  id: 5,
  code: 'MNT-2025/005',
  equipment: 'Trapano a colonna',
  type: 'Ordinaria',
  date: '2025-04-05',
  result: 'Positivo',
  nextDate: '2025-10-05',
  responsible: 'Lorenzo'
}, {
  id: 6,
  code: 'MNT-2025/006',
  equipment: 'Misuratore CO2',
  type: 'Ordinaria',
  date: '2025-01-25',
  result: 'Positivo',
  nextDate: '2026-01-25',
  responsible: 'Tiziano'
}, {
  id: 7,
  code: 'MNT-2025/007',
  equipment: 'Autocarro Iveco Daily',
  type: 'Straordinaria',
  date: '2025-04-01',
  result: 'Positivo',
  nextDate: '2025-10-01',
  responsible: 'Gian Franco'
}];
// ─── Helpers ──────────────────────────────────────────────────────────────────
function getSectionMockData(sectionId: string): any[] {
  switch (sectionId) {
    case 'workers':
      return mockWorkers;
    case 'deadlines':
      return mockDeadlines;
    case 'contracts':
      return mockContracts;
    case 'invoices':
      return mockInvoices;
    case 'training':
      return mockTraining;
    case 'operations':
      return mockOperations;
    case 'non_conformities':
      return mockNonConformities;
    case 'improvements':
      return mockImprovements;
    case 'equipment':
      return mockEquipment;
    case 'maintenance':
      return mockMaintenance;
    default:
      return [];
  }
}
function getSectionSearchFields(sectionId: string): string[] {
  switch (sectionId) {
    case 'workers':
      return ['name', 'role'];
    case 'deadlines':
      return ['description', 'type', 'code'];
    case 'contracts':
      return ['code', 'object'];
    case 'invoices':
      return ['number', 'description'];
    case 'training':
      return ['title', 'code', 'type'];
    case 'operations':
      return ['activityType', 'ambito', 'code'];
    case 'non_conformities':
      return ['number', 'description', 'origin'];
    case 'improvements':
      return ['number', 'subject', 'description'];
    case 'equipment':
      return ['code', 'name', 'type'];
    case 'maintenance':
      return ['code', 'equipment'];
    default:
      return [];
  }
}
function getDefaultColumns(sectionId: string): string[] {
  return (columnsConfigMap[sectionId] || []).map(c => c.key);
}
function getLockedColumns(sectionId: string): string[] {
  return sectionId === 'workers' ? ['name'] : ['actions'];
}
const statusColors: Record<string, string> = {
  da_pianificare: 'neutral',
  pianificato: 'info',
  calendarizzato: 'primary',
  in_svolgimento_attivo: 'success',
  in_svolgimento_sospeso: 'warning',
  in_svolgimento_stop: 'danger',
  completo: 'success'
};
const statusLabels: Record<string, string> = {
  da_pianificare: 'Da pianificare',
  pianificato: 'Pianificata',
  calendarizzato: 'Calendarizzata',
  in_svolgimento_attivo: 'Attiva',
  in_svolgimento_sospeso: 'Sospesa',
  in_svolgimento_stop: 'Stop',
  completo: 'Completa'
};
// ─── Shared Delete Modal ──────────────────────────────────────────────────────
function DeleteModal({
  isOpen,
  onClose,
  onConfirm,
  itemLabel,
  deleteSuccess
}: {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  itemLabel: string;
  deleteSuccess: boolean;
}) {
  return <Modal isOpen={isOpen} onClose={onClose} title="Conferma eliminazione" size="sm" data-id="element-1497">
      <AnimatePresence mode="wait" data-id="element-1498">
        {deleteSuccess ? <motion.div key="success" initial={{
        opacity: 0,
        scale: 0.8
      }} animate={{
        opacity: 1,
        scale: 1
      }} className="flex flex-col items-center justify-center py-12" data-id="element-1499">
            <motion.div initial={{
          scale: 0
        }} animate={{
          scale: 1
        }} transition={{
          type: 'spring',
          stiffness: 150,
          damping: 15,
          delay: 0.1
        }} className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-4" data-id="element-1500">
              <CheckCircle2 className="w-10 h-10 text-green-500" data-id="element-1501" />
            </motion.div>
            <p className="text-lg font-semibold text-slate-800" data-id="element-1502">
              Eliminato con successo
            </p>
          </motion.div> : <motion.div key="confirm" initial={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} data-id="element-1503">
            <div className="space-y-6" data-id="element-1504">
              <div className="bg-red-50 p-4 rounded-lg border border-red-100 flex gap-3" data-id="element-1505">
                <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0" data-id="element-1506" />
                <div data-id="element-1507">
                  <p className="text-sm text-red-700 font-medium" data-id="element-1508">
                    Attenzione: questa azione è irreversibile.
                  </p>
                  <p className="text-sm text-red-600 mt-1" data-id="element-1509">
                    L'elemento <strong data-id="element-1510">{itemLabel}</strong> verrà eliminato
                    definitivamente.
                  </p>
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4" data-id="element-1511">
                <Button variant="flat" color="default" onClick={onClose} data-id="element-1512">
                  Annulla
                </Button>
                <Button color="danger" onClick={onConfirm} startContent={<Trash2 className="h-4 w-4" data-id="element-1514" />} data-id="element-1513">
                  Elimina
                </Button>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </Modal>;
}
// ─── Save Success Overlay ─────────────────────────────────────────────────────
function SaveSuccessView({
  label
}: {
  label: string;
}) {
  return <motion.div key="success" initial={{
    opacity: 0,
    scale: 0.8
  }} animate={{
    opacity: 1,
    scale: 1
  }} className="flex flex-col items-center justify-center py-16" data-id="element-1515">
      <motion.div initial={{
      scale: 0
    }} animate={{
      scale: 1
    }} transition={{
      type: 'spring',
      stiffness: 200,
      damping: 15
    }} className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mb-4" data-id="element-1516">
        <CheckCircle2 className="w-12 h-12 text-green-500" data-id="element-1517" />
      </motion.div>
      <p className="text-lg font-semibold text-slate-800" data-id="element-1518">{label}</p>
    </motion.div>;
}
// ─── Props ────────────────────────────────────────────────────────────────────
interface ClientSectionPageProps {
  sectionId: string;
  clientName: string;
  onBack: () => void;
}
// ─── Main Component ───────────────────────────────────────────────────────────
export function ClientSectionPage({
  sectionId,
  clientName,
  onBack
}: ClientSectionPageProps) {
  const {
    addNotification
  } = useNotifications();
  const config = sectionConfig[sectionId] || sectionConfig['workers'];
  const Icon = config.icon;
  // ─── Shared modal state ───────────────────────────────────────────────────
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isGenerateActivityOpen, setIsGenerateActivityOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [formData, setFormData] = useState<any>({});
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [deleteSuccess, setDeleteSuccess] = useState(false);
  // ─── Toolbar state ────────────────────────────────────────────────────────
  const [showFilters, setShowFilters] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState<string[]>(getDefaultColumns(sectionId));
  const [viewMode, setViewMode] = useState<'list' | 'calendar'>('list');
  // ─── Hooks (called unconditionally at top level) ──────────────────────────
  const sectionData = getSectionMockData(sectionId);
  const searchFields = getSectionSearchFields(sectionId);
  const {
    filters,
    setFilter,
    clearFilters,
    filteredData: filterResult
  } = useFilters(sectionData);
  const {
    searchTerm,
    setSearchTerm,
    filteredData: searchResult
  } = useSearch(filterResult, searchFields);
  const {
    currentPage,
    totalPages,
    paginatedData,
    goToPage,
    pageSize,
    setPageSize,
    totalItems,
    setCurrentPage
  } = usePagination(searchResult);
  // Reset toolbar state when section changes
  useEffect(() => {
    clearFilters();
    setSearchTerm('');
    setShowFilters(false);
    setViewMode('list');
    setVisibleColumns(getDefaultColumns(sectionId));
    setCurrentPage(1);
  }, [sectionId]);
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, searchTerm]);
  // ─── Handlers ────────────────────────────────────────────────────────────
  const handleView = (item: any) => {
    setSelectedItem(item);
    setIsViewOpen(true);
  };
  const handleEdit = (item: any) => {
    setSelectedItem(item);
    setFormData({
      ...item
    });
    setIsEditOpen(true);
  };
  const handleDelete = (item: any) => {
    setSelectedItem(item);
    setIsDeleteOpen(true);
  };
  const handleCreate = () => {
    setSelectedItem(null);
    setFormData({});
    setIsEditOpen(true);
  };
  const handleSave = () => {
    setSaveSuccess(true);
    setTimeout(() => {
      setSaveSuccess(false);
      setIsEditOpen(false);
      addNotification('success', 'Salvato', 'Elemento salvato con successo.');
    }, 1500);
  };
  const confirmDelete = () => {
    setDeleteSuccess(true);
    setTimeout(() => {
      setDeleteSuccess(false);
      setIsDeleteOpen(false);
      addNotification('success', 'Eliminato', 'Elemento eliminato con successo.');
    }, 1500);
  };
  const activeFilterCount = Object.keys(filters).filter(k => (filters as any)[k]).length;
  // ─── Shared Toolbar ───────────────────────────────────────────────────────
  const cols = columnsConfigMap[sectionId] || [];
  const toolbar = <Card className="mb-6" noPadding data-id="element-1519">
      <div className="p-4 flex flex-col gap-4" data-id="element-1520">
        <div className="flex flex-col sm:flex-row gap-4" data-id="element-1521">
          <div className="flex-1" data-id="element-1522">
            <Input placeholder={`Cerca in ${config.title.toLowerCase()}...`} startContent={<Search className="h-4 w-4" data-id="element-1524" />} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} fullWidth data-id="element-1523" />
          </div>
          <div className="flex gap-2" data-id="element-1525">
            <Button variant={showFilters ? 'solid' : 'flat'} color="secondary" onClick={() => setShowFilters(!showFilters)} startContent={<Filter className="h-4 w-4" data-id="element-1527" />} data-id="element-1526">
              Filtri
              {activeFilterCount > 0 && <Badge variant="primary" size="sm" className="ml-1" data-id="element-1528">
                  {activeFilterCount}
                </Badge>}
            </Button>
            <ColumnCustomizer columns={cols} visibleColumns={visibleColumns} onColumnChange={setVisibleColumns} pageKey={`client-${sectionId}`} lockedColumns={getLockedColumns(sectionId)} data-id="element-1529" />
            <Button variant="flat" color="secondary" startContent={<Download className="h-4 w-4" data-id="element-1531" />} data-id="element-1530">
              Esporta
            </Button>
          </div>
        </div>

        {showFilters && <div className="pt-4 border-t border-slate-100 animate-in fade-in slide-in-from-top-2" data-id="element-1532">
            <SectionFilters sectionId={sectionId} filters={filters} setFilter={setFilter} data-id="element-1533" />
            <div className="flex justify-end mt-4" data-id="element-1534">
              <Button variant="flat" color="danger" size="sm" onClick={clearFilters} startContent={<X className="h-4 w-4" data-id="element-1536" />} data-id="element-1535">
                Resetta filtri
              </Button>
            </div>
          </div>}
      </div>
    </Card>;
  // ─── Page Header ──────────────────────────────────────────────────────────
  const header = <>
      <div className="mb-6" data-id="element-1537">
        <button onClick={onBack} className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-seform-navy transition-colors group" data-id="element-1538">
          <ArrowLeft className="h-4 w-4 group-hover:-translate-x-1 transition-transform" data-id="element-1539" />
          <span data-id="element-1540">{clientName}</span>
        </button>
      </div>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4" data-id="element-1541">
        <div data-id="element-1542">
          <div className="flex items-center gap-3 mb-1" data-id="element-1543">
            <Icon className="h-6 w-6 text-seform-navy" data-id="element-1544" />
            <h1 className="text-3xl font-bold text-seform-navy" data-id="element-1545">
              {config.title}
            </h1>
          </div>
          <p className="text-slate-500 mt-1" data-id="element-1546">{config.description}</p>
        </div>
        <div className="flex items-center gap-3" data-id="element-1547">
          {sectionId === 'deadlines' && <div className="bg-slate-100 p-1 rounded-xl flex items-center" data-id="element-1548">
              <button onClick={() => setViewMode('calendar')} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${viewMode === 'calendar' ? 'bg-white text-seform-navy shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-1549">
                <CalendarIcon className="h-4 w-4 inline-block mr-1.5" data-id="element-1550" />{' '}
                Calendario
              </button>
              <button onClick={() => setViewMode('list')} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${viewMode === 'list' ? 'bg-white text-seform-navy shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-1551">
                <List className="h-4 w-4 inline-block mr-1.5" data-id="element-1552" /> Lista
              </button>
            </div>}
          <Button onClick={handleCreate} startContent={<Plus className="h-4 w-4" data-id="element-1554" />} data-id="element-1553">
            {config.newLabel}
          </Button>
        </div>
      </div>
    </>;
  // ─── Action Buttons (shared) ──────────────────────────────────────────────
  const actionButtons = (item: any, label: string) => <div className="flex items-center gap-1" data-id="element-1555">
      <Tooltip content={`Visualizza ${label}`} data-id="element-1556">
        <Button variant="light" size="sm" className="!px-1" onClick={() => handleView(item)} data-id="element-1557">
          <Eye className="h-4 w-4 text-slate-500" data-id="element-1558" />
        </Button>
      </Tooltip>
      <Tooltip content={`Modifica ${label}`} data-id="element-1559">
        <Button variant="light" size="sm" className="!px-1" onClick={() => handleEdit(item)} data-id="element-1560">
          <Edit className="h-4 w-4 text-seform-orange" data-id="element-1561" />
        </Button>
      </Tooltip>
      <Tooltip content={`Elimina ${label}`} data-id="element-1562">
        <Button variant="light" size="sm" className="!px-1" onClick={() => handleDelete(item)} data-id="element-1563">
          <Trash2 className="h-4 w-4 text-red-500" data-id="element-1564" />
        </Button>
      </Tooltip>
    </div>;
  // ─── Workers Section ──────────────────────────────────────────────────────
  if (sectionId === 'workers') {
    return <Layout data-id="element-1565">
        {header}
        {toolbar}
        <Card noPadding className="overflow-hidden" data-id="element-1566">
          <div className="overflow-x-auto" data-id="element-1567">
            <table className="w-full text-left border-collapse table-auto" data-id="element-1568">
              <thead data-id="element-1569">
                <tr className="bg-slate-50/50 border-b border-slate-100 text-xs uppercase tracking-wider text-slate-500 font-semibold" data-id="element-1570">
                  {cols.filter(c => visibleColumns.includes(c.key)).map(col => <th key={col.key} className="px-4 py-4" data-id="element-1571">
                        {col.label}
                      </th>)}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-1572">
                {paginatedData.map(item => <tr key={item.id} className="hover:bg-slate-50/80 transition-colors group" data-id="element-1573">
                    {visibleColumns.includes('name') && <td className="px-4 py-4 text-sm font-medium text-slate-900" data-id="element-1574">
                        {item.name}
                      </td>}
                    {visibleColumns.includes('role') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-1575">
                        {item.role}
                      </td>}
                    {visibleColumns.includes('trainingStatus') && <td className="px-4 py-4" data-id="element-1576">
                        <Badge variant={item.trainingStatus === 'valid' ? 'success' : item.trainingStatus === 'expiring' ? 'warning' : 'danger'} data-id="element-1577">
                          {item.trainingStatus === 'valid' ? 'In regola' : item.trainingStatus === 'expiring' ? 'In scadenza' : 'Scaduto'}
                        </Badge>
                      </td>}
                    {visibleColumns.includes('healthStatus') && <td className="px-4 py-4" data-id="element-1578">
                        <Badge variant="success" data-id="element-1579">Idoneo</Badge>
                      </td>}
                    {visibleColumns.includes('nextExpiry') && <td className="px-4 py-4 text-sm font-mono text-slate-600" data-id="element-1580">
                        {item.nextExpiry}
                      </td>}
                    {visibleColumns.includes('actions') && <td className="px-4 py-4 pr-6" data-id="element-1581">
                        {actionButtons(item, 'lavoratore')}
                      </td>}
                  </tr>)}
              </tbody>
            </table>
          </div>
          <div className="border-t border-slate-100" data-id="element-1582">
            <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={goToPage} totalItems={totalItems} pageSize={pageSize} onPageSizeChange={setPageSize} data-id="element-1583" />
          </div>
        </Card>

        <Modal isOpen={isViewOpen} onClose={() => setIsViewOpen(false)} title="Scheda lavoratore" size="lg" data-id="element-1584">
          {selectedItem && <div className="space-y-6" data-id="element-1585">
              <div className="grid grid-cols-2 gap-4" data-id="element-1586">
                <div data-id="element-1587">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-1588">
                    Nome
                  </span>
                  <p className="font-medium mt-1" data-id="element-1589">{selectedItem.name}</p>
                </div>
                <div data-id="element-1590">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-1591">
                    Mansione
                  </span>
                  <p className="font-medium mt-1" data-id="element-1592">{selectedItem.role}</p>
                </div>
                <div data-id="element-1593">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-1594">
                    Formazione
                  </span>
                  <div className="mt-1" data-id="element-1595">
                    <Badge variant={selectedItem.trainingStatus === 'valid' ? 'success' : selectedItem.trainingStatus === 'expiring' ? 'warning' : 'danger'} data-id="element-1596">
                      {selectedItem.trainingStatus === 'valid' ? 'In regola' : selectedItem.trainingStatus === 'expiring' ? 'In scadenza' : 'Scaduto'}
                    </Badge>
                  </div>
                </div>
                <div data-id="element-1597">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-1598">
                    Sorveglianza sanitaria
                  </span>
                  <div className="mt-1" data-id="element-1599">
                    <Badge variant="success" data-id="element-1600">Idoneo</Badge>
                  </div>
                </div>
                <div data-id="element-1601">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-1602">
                    Prossima scadenza
                  </span>
                  <p className="font-mono font-medium mt-1" data-id="element-1603">
                    {selectedItem.nextExpiry}
                  </p>
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-1604">
                <Button variant="flat" color="default" onClick={() => setIsViewOpen(false)} data-id="element-1605">
                  Chiudi
                </Button>
                <Button onClick={() => {
              setIsViewOpen(false);
              handleEdit(selectedItem);
            }} startContent={<Edit className="h-4 w-4" data-id="element-1607" />} data-id="element-1606">
                  Modifica
                </Button>
              </div>
            </div>}
        </Modal>

        <Modal isOpen={isEditOpen} onClose={() => setIsEditOpen(false)} title={selectedItem ? 'Modifica lavoratore' : 'Nuovo lavoratore'} size="lg" data-id="element-1608">
          <AnimatePresence mode="wait" data-id="element-1609">
            {saveSuccess ? <SaveSuccessView label="Lavoratore salvato con successo" data-id="element-1610" /> : <motion.div key="form" initial={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} data-id="element-1611">
                <div className="space-y-6" data-id="element-1612">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1613">
                    <Input label="Nome e cognome" value={formData.name || ''} onChange={e => setFormData({
                  ...formData,
                  name: e.target.value
                })} required data-id="element-1614" />
                    <Input label="Mansione" value={formData.role || ''} onChange={e => setFormData({
                  ...formData,
                  role: e.target.value
                })} data-id="element-1615" />
                    <Select label="Stato formazione" value={formData.trainingStatus || ''} onChange={e => setFormData({
                  ...formData,
                  trainingStatus: e.target.value
                })} options={[{
                  value: 'valid',
                  label: 'In regola'
                }, {
                  value: 'expiring',
                  label: 'In scadenza'
                }, {
                  value: 'expired',
                  label: 'Scaduto'
                }]} data-id="element-1616" />
                    <Input label="Prossima scadenza" type="date" value={formData.nextExpiry || ''} onChange={e => setFormData({
                  ...formData,
                  nextExpiry: e.target.value
                })} data-id="element-1617" />
                  </div>
                  <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-1618">
                    <Button variant="flat" color="default" onClick={() => setIsEditOpen(false)} data-id="element-1619">
                      Annulla
                    </Button>
                    <Button onClick={handleSave} startContent={<Save className="h-4 w-4" data-id="element-1621" />} data-id="element-1620">
                      Salva lavoratore
                    </Button>
                  </div>
                </div>
              </motion.div>}
          </AnimatePresence>
        </Modal>

        <DeleteModal isOpen={isDeleteOpen} onClose={() => setIsDeleteOpen(false)} onConfirm={confirmDelete} itemLabel={selectedItem?.name || ''} deleteSuccess={deleteSuccess} data-id="element-1622" />
      </Layout>;
  }
  // ─── Deadlines Section ────────────────────────────────────────────────────
  if (sectionId === 'deadlines') {
    return <Layout data-id="element-1623">
        {header}
        {viewMode === 'list' ? <>
            {toolbar}
            <Card noPadding className="overflow-hidden" data-id="element-1624">
              <div className="overflow-x-auto" data-id="element-1625">
                <table className="w-full text-left border-collapse table-auto" data-id="element-1626">
                  <thead data-id="element-1627">
                    <tr className="bg-slate-50/50 border-b border-slate-100 text-xs uppercase tracking-wider text-slate-500 font-semibold" data-id="element-1628">
                      {cols.filter(c => visibleColumns.includes(c.key)).map(col => <th key={col.key} className="px-4 py-4" data-id="element-1629">
                            {col.label}
                          </th>)}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100" data-id="element-1630">
                    {paginatedData.map(item => <tr key={item.id} className="hover:bg-slate-50/80 transition-colors group" data-id="element-1631">
                        {visibleColumns.includes('code') && <td className="px-4 py-4 font-mono text-xs text-slate-500" data-id="element-1632">
                            {item.code}
                          </td>}
                        {visibleColumns.includes('description') && <td className="px-4 py-4 text-sm font-medium text-slate-900" data-id="element-1633">
                            {item.description}
                          </td>}
                        {visibleColumns.includes('type') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-1634">
                            {item.type}
                          </td>}
                        {visibleColumns.includes('date') && <td className="px-4 py-4 text-sm font-mono text-slate-600" data-id="element-1635">
                            {item.date}
                          </td>}
                        {visibleColumns.includes('notifyClient') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-1636">
                            {item.notifyClient}
                          </td>}
                        {visibleColumns.includes('responsible') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-1637">
                            {item.responsible}
                          </td>}
                        {visibleColumns.includes('status') && <td className="px-4 py-4 whitespace-nowrap" data-id="element-1638">
                            <Badge variant={item.status === 'Urgente' ? 'danger' : item.status === 'In scadenza' ? 'warning' : item.status === 'Completata' ? 'success' : 'info'} data-id="element-1639">
                              {item.status}
                            </Badge>
                          </td>}
                        {visibleColumns.includes('actions') && <td className="px-4 py-4 pr-6" data-id="element-1640">
                            {actionButtons(item, 'scadenza')}
                          </td>}
                      </tr>)}
                  </tbody>
                </table>
              </div>
              <div className="border-t border-slate-100" data-id="element-1641">
                <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={goToPage} totalItems={totalItems} pageSize={pageSize} onPageSizeChange={setPageSize} data-id="element-1642" />
              </div>
            </Card>
          </> : <CalendarView events={searchResult.map((item: any) => ({
        id: String(item.id),
        title: item.description,
        date: item.date,
        type: item.type,
        priority: item.status === 'Urgente' ? 'high' : item.status === 'In scadenza' ? 'medium' : 'low',
        client: clientName
      }))} onEventClick={(event: any) => {
        const found = mockDeadlines.find(d => String(d.id) === event.id);
        if (found) handleView(found);
      }} data-id="element-1643" />}

        <Modal isOpen={isViewOpen} onClose={() => setIsViewOpen(false)} title="Dettaglio scadenza" size="lg" data-id="element-1644">
          {selectedItem && <div className="space-y-6" data-id="element-1645">
              <div className="grid grid-cols-2 gap-4" data-id="element-1646">
                <div data-id="element-1647">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-1648">
                    Codice
                  </span>
                  <p className="font-mono font-medium mt-1" data-id="element-1649">
                    {selectedItem.code}
                  </p>
                </div>
                <div data-id="element-1650">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-1651">
                    Stato
                  </span>
                  <div className="mt-1" data-id="element-1652">
                    <Badge variant={selectedItem.status === 'Urgente' ? 'danger' : selectedItem.status === 'In scadenza' ? 'warning' : 'info'} data-id="element-1653">
                      {selectedItem.status}
                    </Badge>
                  </div>
                </div>
                <div className="col-span-full" data-id="element-1654">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-1655">
                    Descrizione
                  </span>
                  <p className="font-medium mt-1" data-id="element-1656">{selectedItem.description}</p>
                </div>
                <div data-id="element-1657">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-1658">
                    Tipologia
                  </span>
                  <p className="font-medium mt-1" data-id="element-1659">{selectedItem.type}</p>
                </div>
                <div data-id="element-1660">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-1661">
                    Data scadenza
                  </span>
                  <p className="font-mono font-medium mt-1" data-id="element-1662">
                    {selectedItem.date}
                  </p>
                </div>
                <div data-id="element-1663">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-1664">
                    Responsabile
                  </span>
                  <p className="font-medium mt-1" data-id="element-1665">{selectedItem.responsible}</p>
                </div>
                <div data-id="element-1666">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-1667">
                    Notifica cliente
                  </span>
                  <p className="font-medium mt-1" data-id="element-1668">
                    {selectedItem.notifyClient}
                  </p>
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-1669">
                <Button variant="flat" color="default" onClick={() => setIsViewOpen(false)} data-id="element-1670">
                  Chiudi
                </Button>
                <Button onClick={() => {
              setIsViewOpen(false);
              handleEdit(selectedItem);
            }} startContent={<Edit className="h-4 w-4" data-id="element-1672" />} data-id="element-1671">
                  Modifica
                </Button>
              </div>
            </div>}
        </Modal>

        <Modal isOpen={isEditOpen} onClose={() => setIsEditOpen(false)} title={selectedItem ? 'Modifica scadenza' : 'Nuova scadenza'} size="lg" data-id="element-1673">
          <AnimatePresence mode="wait" data-id="element-1674">
            {saveSuccess ? <SaveSuccessView label="Scadenza salvata con successo" data-id="element-1675" /> : <motion.div key="form" initial={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} data-id="element-1676">
                <div className="space-y-6" data-id="element-1677">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1678">
                    <Input label="Codice" value={formData.code || ''} onChange={e => setFormData({
                  ...formData,
                  code: e.target.value
                })} data-id="element-1679" />
                    <Select label="Tipologia" value={formData.type || ''} onChange={e => setFormData({
                  ...formData,
                  type: e.target.value
                })} options={[{
                  value: 'Riunione annuale',
                  label: 'Riunione annuale'
                }, {
                  value: 'Prova di emergenza',
                  label: 'Prova di emergenza'
                }, {
                  value: 'Tampone alimentare',
                  label: 'Tampone alimentare'
                }, {
                  value: 'Campionamento alimentare',
                  label: 'Campionamento alimentare'
                }, {
                  value: 'Audit interno',
                  label: 'Audit interno'
                }, {
                  value: 'Report annuale',
                  label: 'Report annuale'
                }]} required data-id="element-1680" />
                    <div className="col-span-full" data-id="element-1681">
                      <Textarea label="Descrizione" value={formData.description || ''} onChange={e => setFormData({
                    ...formData,
                    description: e.target.value
                  })} required data-id="element-1682" />
                    </div>
                    <DatePicker label="Data scadenza" value={formData.date || ''} onChange={e => setFormData({
                  ...formData,
                  date: e.target.value
                })} data-id="element-1683" />
                    <Input label="Responsabile" value={formData.responsible || ''} onChange={e => setFormData({
                  ...formData,
                  responsible: e.target.value
                })} data-id="element-1684" />
                    <Select label="Stato" value={formData.status || ''} onChange={e => setFormData({
                  ...formData,
                  status: e.target.value
                })} options={[{
                  value: 'Pianificata',
                  label: 'Pianificata'
                }, {
                  value: 'In scadenza',
                  label: 'In scadenza'
                }, {
                  value: 'Urgente',
                  label: 'Urgente'
                }, {
                  value: 'Completata',
                  label: 'Completata'
                }]} required data-id="element-1685" />
                    <div className="flex items-center gap-2 mt-2" data-id="element-1686">
                      <input type="checkbox" id="notifyClientEdit" className="rounded text-seform-orange focus:ring-seform-orange" checked={formData.notifyClient === 'Sì'} onChange={e => setFormData({
                    ...formData,
                    notifyClient: e.target.checked ? 'Sì' : 'No'
                  })} data-id="element-1687" />
                      <label htmlFor="notifyClientEdit" className="text-sm text-slate-700" data-id="element-1688">
                        Notifica cliente
                      </label>
                    </div>
                  </div>
                  <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-1689">
                    <Button variant="flat" color="default" onClick={() => setIsEditOpen(false)} data-id="element-1690">
                      Annulla
                    </Button>
                    <Button onClick={handleSave} startContent={<Save className="h-4 w-4" data-id="element-1692" />} data-id="element-1691">
                      Salva scadenza
                    </Button>
                  </div>
                </div>
              </motion.div>}
          </AnimatePresence>
        </Modal>

        <DeleteModal isOpen={isDeleteOpen} onClose={() => setIsDeleteOpen(false)} onConfirm={confirmDelete} itemLabel={selectedItem?.description || ''} deleteSuccess={deleteSuccess} data-id="element-1693" />
      </Layout>;
  }
  // ─── Contracts Section ────────────────────────────────────────────────────
  if (sectionId === 'contracts') {
    return <Layout data-id="element-1694">
        {header}
        {toolbar}
        <Card noPadding className="overflow-hidden" data-id="element-1695">
          <div className="overflow-x-auto" data-id="element-1696">
            <table className="w-full text-left border-collapse table-auto" data-id="element-1697">
              <thead data-id="element-1698">
                <tr className="bg-slate-50/50 border-b border-slate-100 text-xs uppercase tracking-wider text-slate-500 font-semibold" data-id="element-1699">
                  {cols.filter(c => visibleColumns.includes(c.key)).map(col => <th key={col.key} className="px-4 py-4" data-id="element-1700">
                        {col.label}
                      </th>)}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-1701">
                {paginatedData.map(item => <tr key={item.id} className="hover:bg-slate-50/80 transition-colors group" data-id="element-1702">
                    {visibleColumns.includes('code') && <td className="px-4 py-4 font-mono text-xs text-slate-500" data-id="element-1703">
                        {item.code}
                      </td>}
                    {visibleColumns.includes('object') && <td className="px-4 py-4 text-sm font-medium text-slate-900" data-id="element-1704">
                        {item.object}
                      </td>}
                    {visibleColumns.includes('type') && <td className="px-4 py-4" data-id="element-1705">
                        <Badge variant={item.type === 'Contratto' ? 'primary' : 'neutral'} data-id="element-1706">
                          {item.type}
                        </Badge>
                      </td>}
                    {visibleColumns.includes('value') && <td className="px-4 py-4 text-sm font-medium text-slate-900" data-id="element-1707">
                        {item.value}
                      </td>}
                    {visibleColumns.includes('status') && <td className="px-4 py-4" data-id="element-1708">
                        <Badge variant={item.status === 'Attivo' ? 'success' : item.status === 'In attesa' ? 'warning' : item.status === 'Scaduto' ? 'danger' : 'neutral'} data-id="element-1709">
                          {item.status}
                        </Badge>
                      </td>}
                    {visibleColumns.includes('date') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-1710">
                        {item.date}
                      </td>}
                    {visibleColumns.includes('actions') && <td className="px-4 py-4 pr-6" data-id="element-1711">
                        {actionButtons(item, 'contratto')}
                      </td>}
                  </tr>)}
              </tbody>
            </table>
          </div>
          <div className="border-t border-slate-100" data-id="element-1712">
            <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={goToPage} totalItems={totalItems} pageSize={pageSize} onPageSizeChange={setPageSize} data-id="element-1713" />
          </div>
        </Card>

        <Modal isOpen={isViewOpen} onClose={() => setIsViewOpen(false)} title="Dettaglio contratto" size="2xl" data-id="element-1714">
          {selectedItem && <div className="space-y-8" data-id="element-1715">
              <section data-id="element-1716">
                <h4 className="text-sm font-bold text-seform-navy uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-1717">
                  <FileText className="h-4 w-4" data-id="element-1718" /> Dati generali
                </h4>
                <div className="grid grid-cols-2 gap-4" data-id="element-1719">
                  <div data-id="element-1720">
                    <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-1721">
                      Codice
                    </span>
                    <p className="font-mono font-medium mt-1" data-id="element-1722">
                      {selectedItem.code}
                    </p>
                  </div>
                  <div data-id="element-1723">
                    <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-1724">
                      Cliente
                    </span>
                    <p className="font-medium mt-1" data-id="element-1725">{clientName}</p>
                  </div>
                  <div data-id="element-1726">
                    <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-1727">
                      Prodotto
                    </span>
                    <p className="font-medium mt-1" data-id="element-1728">{selectedItem.object}</p>
                  </div>
                  <div data-id="element-1729">
                    <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-1730">
                      Tipologia
                    </span>
                    <p className="font-medium mt-1" data-id="element-1731">
                      {selectedItem.tipologia || 'Sicurezza'}
                    </p>
                  </div>
                  <div data-id="element-1732">
                    <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-1733">
                      Responsabile sviluppo
                    </span>
                    <p className="font-medium mt-1" data-id="element-1734">
                      {selectedItem.responsabileSviluppo || 'Alessio'}
                    </p>
                  </div>
                  <div data-id="element-1735">
                    <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-1736">
                      Provenienza
                    </span>
                    <p className="font-medium mt-1" data-id="element-1737">
                      {selectedItem.provenienza || 'Commerciale SEF'}
                    </p>
                  </div>
                  <div data-id="element-1738">
                    <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-1739">
                      Corrispettivo provenienza
                    </span>
                    <p className="font-mono font-medium mt-1" data-id="element-1740">
                      {selectedItem.corrispettivoProvenienza || '€ 500'}
                    </p>
                  </div>
                </div>
              </section>

              <div className="h-px bg-slate-100" data-id="element-1741"></div>

              <section data-id="element-1742">
                <h4 className="text-sm font-bold text-seform-navy uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-1743">
                  <AlertTriangle className="h-4 w-4" data-id="element-1744" /> Stato e workflow
                </h4>
                <div className="grid grid-cols-2 gap-4 mb-4" data-id="element-1745">
                  <div data-id="element-1746">
                    <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-1747">
                      Stato attuale
                    </span>
                    <div className="mt-1" data-id="element-1748">
                      <Badge variant={selectedItem.status === 'Attivo' || selectedItem.status === 'accettato' ? 'success' : selectedItem.status === 'In attesa' || selectedItem.status === 'in_valutazione' ? 'warning' : selectedItem.status === 'Scaduto' || selectedItem.status === 'non_accettato' || selectedItem.status === 'revocato' ? 'danger' : 'neutral'} data-id="element-1749">
                        {selectedItem.status}
                      </Badge>
                    </div>
                  </div>
                  <div data-id="element-1750">
                    <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-1751">
                      Data cambio stato
                    </span>
                    <p className="font-medium mt-1" data-id="element-1752">
                      {selectedItem.dataCambioStato || selectedItem.date || '15/03/2025'}
                    </p>
                  </div>
                </div>

                {/* Conditional status-specific fields */}
                {['Attivo', 'accettato'].includes(selectedItem.status) && <div className="bg-green-50 p-4 rounded-lg border border-green-100" data-id="element-1753">
                    <h4 className="text-sm font-bold text-green-800 uppercase tracking-wider mb-3" data-id="element-1754">
                      Dettagli accettazione
                    </h4>
                    <div className="grid grid-cols-2 gap-4" data-id="element-1755">
                      <div data-id="element-1756">
                        <span className="text-xs font-semibold text-green-600 uppercase" data-id="element-1757">
                          Data di accettazione
                        </span>
                        <p className="font-medium text-green-900" data-id="element-1758">
                          {selectedItem.acceptanceDate || '15/03/2025'}
                        </p>
                      </div>
                    </div>
                  </div>}

                {['non_accettato', 'rejected'].includes(selectedItem.status) && <div className="bg-red-50 p-4 rounded-lg border border-red-100" data-id="element-1759">
                    <h4 className="text-sm font-bold text-red-800 uppercase tracking-wider mb-3" data-id="element-1760">
                      Dettagli non accettazione
                    </h4>
                    <div className="grid grid-cols-2 gap-4" data-id="element-1761">
                      <div data-id="element-1762">
                        <span className="text-xs font-semibold text-red-600 uppercase" data-id="element-1763">
                          Data non accettazione
                        </span>
                        <p className="font-medium text-red-900" data-id="element-1764">
                          {selectedItem.rejectionDate || '20/02/2025'}
                        </p>
                      </div>
                      <div data-id="element-1765">
                        <span className="text-xs font-semibold text-red-600 uppercase" data-id="element-1766">
                          Motivazione
                        </span>
                        <p className="font-medium text-red-900" data-id="element-1767">
                          {selectedItem.rejectionReason || 'Budget insufficiente'}
                        </p>
                      </div>
                    </div>
                  </div>}

                {['revocato', 'revoked'].includes(selectedItem.status) && <div className="bg-orange-50 p-4 rounded-lg border border-orange-100" data-id="element-1768">
                    <h4 className="text-sm font-bold text-orange-800 uppercase tracking-wider mb-3" data-id="element-1769">
                      Dettagli revoca
                    </h4>
                    <div className="grid grid-cols-2 gap-4" data-id="element-1770">
                      <div data-id="element-1771">
                        <span className="text-xs font-semibold text-orange-600 uppercase" data-id="element-1772">
                          Data revoca
                        </span>
                        <p className="font-medium text-orange-900" data-id="element-1773">
                          {selectedItem.revokeDate || '10/01/2025'}
                        </p>
                      </div>
                      <div data-id="element-1774">
                        <span className="text-xs font-semibold text-orange-600 uppercase" data-id="element-1775">
                          Motivazione
                        </span>
                        <p className="font-medium text-orange-900" data-id="element-1776">
                          {selectedItem.revokeReason || 'Cambio fornitore'}
                        </p>
                      </div>
                    </div>
                  </div>}
              </section>

              <div className="h-px bg-slate-100" data-id="element-1777"></div>

              <section data-id="element-1778">
                <h4 className="text-sm font-bold text-seform-navy uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-1779">
                  <Euro className="h-4 w-4" data-id="element-1780" /> Dettagli economici
                </h4>
                <div className="grid grid-cols-2 gap-4" data-id="element-1781">
                  <div data-id="element-1782">
                    <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-1783">
                      Importo
                    </span>
                    <p className="font-mono font-bold text-seform-navy mt-1" data-id="element-1784">
                      {selectedItem.value}
                    </p>
                  </div>
                  <div data-id="element-1785">
                    <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-1786">
                      Durata (mesi)
                    </span>
                    <p className="font-medium mt-1" data-id="element-1787">
                      {selectedItem.durata || '12'}
                    </p>
                  </div>
                  <div data-id="element-1788">
                    <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-1789">
                      Data preventivo
                    </span>
                    <p className="font-medium mt-1" data-id="element-1790">
                      {selectedItem.dataPreventivo || selectedItem.date || '10/01/2025'}
                    </p>
                  </div>
                  <div data-id="element-1791">
                    <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-1792">
                      Scadenza
                    </span>
                    <p className="font-medium mt-1" data-id="element-1793">
                      {selectedItem.expiry || selectedItem.date}
                    </p>
                  </div>
                  <div data-id="element-1794">
                    <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-1795">
                      Tacito rinnovo
                    </span>
                    <p className="font-medium mt-1" data-id="element-1796">
                      {selectedItem.tacitRenewal !== false ? 'Sì' : 'No'}
                    </p>
                  </div>
                </div>
              </section>

              <div className="flex justify-between items-center pt-4 border-t border-slate-100" data-id="element-1797">
                <div data-id="element-1798">
                  {['Attivo', 'accettato'].includes(selectedItem.status) && <Button variant="flat" color="success" startContent={<ClipboardList className="h-4 w-4" data-id="element-1800" />} onClick={() => setIsGenerateActivityOpen(true)} data-id="element-1799">
                      Genera attività operativa
                    </Button>}
                </div>
                <div className="flex gap-3" data-id="element-1801">
                  <Button variant="flat" color="default" onClick={() => setIsViewOpen(false)} data-id="element-1802">
                    Chiudi
                  </Button>
                  <Button onClick={() => {
                setIsViewOpen(false);
                handleEdit(selectedItem);
              }} startContent={<Edit className="h-4 w-4" data-id="element-1804" />} data-id="element-1803">
                    Modifica
                  </Button>
                </div>
              </div>
            </div>}
        </Modal>

        <GenerateActivityWizardModal isOpen={isGenerateActivityOpen} onClose={() => setIsGenerateActivityOpen(false)} data-id="element-1805" />

        <Modal isOpen={isEditOpen} onClose={() => setIsEditOpen(false)} title={selectedItem ? 'Modifica contratto' : 'Nuovo contratto'} size="2xl" data-id="element-1806">
          <AnimatePresence mode="wait" data-id="element-1807">
            {saveSuccess ? <SaveSuccessView label="Contratto salvato con successo" data-id="element-1808" /> : <motion.div key="form" initial={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} data-id="element-1809">
                <div className="space-y-8" data-id="element-1810">
                  <section data-id="element-1811">
                    <h4 className="text-sm font-bold text-seform-navy uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-1812">
                      <FileText className="h-4 w-4" data-id="element-1813" /> Dati generali
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1814">
                      <ProgressiveNumberInput type="contract" value={formData.code || ''} onChange={val => setFormData({
                    ...formData,
                    code: val
                  })} disabled={!!selectedItem} data-id="element-1815" />
                      <Input label="Cliente" value={clientName} disabled data-id="element-1816" />
                      <Input label="Prodotto / Oggetto" value={formData.object || ''} onChange={e => setFormData({
                    ...formData,
                    object: e.target.value
                  })} required data-id="element-1817" />
                      <Select label="Tipologia" value={formData.tipologia || ''} onChange={e => setFormData({
                    ...formData,
                    tipologia: e.target.value
                  })} options={[{
                    value: '',
                    label: 'Seleziona...'
                  }, {
                    value: 'Sicurezza',
                    label: 'Sicurezza'
                  }, {
                    value: 'Privacy',
                    label: 'Privacy'
                  }, {
                    value: 'HACCP',
                    label: 'HACCP'
                  }, {
                    value: 'Altro',
                    label: 'Altro'
                  }]} data-id="element-1818" />
                      <Select label="Responsabile sviluppo" value={formData.responsabileSviluppo || ''} onChange={e => setFormData({
                    ...formData,
                    responsabileSviluppo: e.target.value
                  })} options={[{
                    value: 'alessio',
                    label: 'Alessio'
                  }, {
                    value: 'gianfranco',
                    label: 'Gian Franco'
                  }, {
                    value: 'lorenzo',
                    label: 'Lorenzo'
                  }, {
                    value: 'tiziano',
                    label: 'Tiziano'
                  }]} required data-id="element-1819" />
                      <Select label="Provenienza" value={formData.provenienza || ''} onChange={e => setFormData({
                    ...formData,
                    provenienza: e.target.value
                  })} options={[{
                    value: '',
                    label: 'Seleziona...'
                  }, {
                    value: 'Commerciale SEF',
                    label: 'Commerciale SEF'
                  }, {
                    value: 'Esterni e partner',
                    label: 'Esterni e partner'
                  }, {
                    value: 'Altro',
                    label: 'Altro'
                  }]} data-id="element-1820" />
                      <Input label="Corrispettivo provenienza (€)" placeholder="Es. 500,00" value={formData.corrispettivoProvenienza?.replace('€ ', '') || ''} onChange={e => setFormData({
                    ...formData,
                    corrispettivoProvenienza: e.target.value
                  })} data-id="element-1821" />
                    </div>
                  </section>

                  <div className="h-px bg-slate-100" data-id="element-1822"></div>

                  <section data-id="element-1823">
                    <h4 className="text-sm font-bold text-seform-navy uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-1824">
                      <AlertTriangle className="h-4 w-4" data-id="element-1825" /> Stato e workflow
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1826">
                      <Select label="Stato attuale" value={formData.status || ''} onChange={e => setFormData({
                    ...formData,
                    status: e.target.value
                  })} options={[{
                    value: 'da_proporre',
                    label: 'Da proporre'
                  }, {
                    value: 'in_valutazione',
                    label: 'In valutazione'
                  }, {
                    value: 'da_sollecitare',
                    label: 'Da sollecitare'
                  }, {
                    value: 'accettato',
                    label: 'Accettato'
                  }, {
                    value: 'non_accettato',
                    label: 'Non accettato'
                  }, {
                    value: 'revocato',
                    label: 'Revocato'
                  }, {
                    value: 'Attivo',
                    label: 'Attivo (Legacy)'
                  }, {
                    value: 'In attesa',
                    label: 'In attesa (Legacy)'
                  }, {
                    value: 'Bozza',
                    label: 'Bozza (Legacy)'
                  }, {
                    value: 'Scaduto',
                    label: 'Scaduto (Legacy)'
                  }]} required data-id="element-1827" />
                      <Input label="Data cambio stato" type="date" value={formData.dataCambioStato || new Date().toISOString().split('T')[0]} onChange={e => setFormData({
                    ...formData,
                    dataCambioStato: e.target.value
                  })} data-id="element-1828" />

                      {['accettato', 'Attivo'].includes(formData.status) && <Input label="Data di accettazione" type="date" value={formData.acceptanceDate || ''} onChange={e => setFormData({
                    ...formData,
                    acceptanceDate: e.target.value
                  })} data-id="element-1829" />}

                      {['non_accettato', 'rejected'].includes(formData.status) && <>
                          <Input label="Data non accettazione" type="date" value={formData.rejectionDate || ''} onChange={e => setFormData({
                      ...formData,
                      rejectionDate: e.target.value
                    })} data-id="element-1830" />
                          <div className="col-span-full" data-id="element-1831">
                            <Input label="Motivazione" placeholder="Inserisci la motivazione..." value={formData.rejectionReason || ''} onChange={e => setFormData({
                        ...formData,
                        rejectionReason: e.target.value
                      })} data-id="element-1832" />
                          </div>
                        </>}

                      {['revocato', 'revoked'].includes(formData.status) && <>
                          <Input label="Data revoca" type="date" value={formData.revokeDate || ''} onChange={e => setFormData({
                      ...formData,
                      revokeDate: e.target.value
                    })} data-id="element-1833" />
                          <div className="col-span-full" data-id="element-1834">
                            <Input label="Motivazione" placeholder="Inserisci la motivazione della revoca..." value={formData.revokeReason || ''} onChange={e => setFormData({
                        ...formData,
                        revokeReason: e.target.value
                      })} data-id="element-1835" />
                          </div>
                        </>}
                    </div>
                  </section>

                  <div className="h-px bg-slate-100" data-id="element-1836"></div>

                  <section data-id="element-1837">
                    <h4 className="text-sm font-bold text-seform-navy uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-1838">
                      <Euro className="h-4 w-4" data-id="element-1839" /> Dettagli economici
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1840">
                      <Input label="Importo (€)" type="number" placeholder="0.00" value={formData.value?.replace('€ ', '') || ''} onChange={e => setFormData({
                    ...formData,
                    value: e.target.value
                  })} required data-id="element-1841" />
                      <Input label="Durata (mesi)" type="number" value={formData.durata || '12'} onChange={e => setFormData({
                    ...formData,
                    durata: e.target.value
                  })} required data-id="element-1842" />
                      <Input label="Data preventivo" type="date" value={formData.dataPreventivo || formData.date || ''} onChange={e => setFormData({
                    ...formData,
                    dataPreventivo: e.target.value
                  })} data-id="element-1843" />
                      <div className="flex items-center gap-2 pt-6" data-id="element-1844">
                        <input type="checkbox" id="renewal" className="rounded border-slate-300 text-seform-orange focus:ring-seform-orange" checked={formData.tacitRenewal ?? false} onChange={e => setFormData({
                      ...formData,
                      tacitRenewal: e.target.checked
                    })} data-id="element-1845" />
                        <label htmlFor="renewal" className="text-sm text-slate-700" data-id="element-1846">
                          Tacito rinnovo
                        </label>
                      </div>
                    </div>
                  </section>

                  <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-1847">
                    <Button variant="flat" color="default" onClick={() => setIsEditOpen(false)} data-id="element-1848">
                      Annulla
                    </Button>
                    <Button onClick={handleSave} startContent={<Save className="h-4 w-4" data-id="element-1850" />} data-id="element-1849">
                      Salva contratto
                    </Button>
                  </div>
                </div>
              </motion.div>}
          </AnimatePresence>
        </Modal>

        <DeleteModal isOpen={isDeleteOpen} onClose={() => setIsDeleteOpen(false)} onConfirm={confirmDelete} itemLabel={selectedItem?.code || ''} deleteSuccess={deleteSuccess} data-id="element-1851" />
      </Layout>;
  }
  // ─── Invoices Section ─────────────────────────────────────────────────────
  if (sectionId === 'invoices') {
    return <Layout data-id="element-1852">
        {header}
        {toolbar}
        <Card noPadding className="overflow-hidden" data-id="element-1853">
          <div className="overflow-x-auto" data-id="element-1854">
            <table className="w-full text-left border-collapse table-auto" data-id="element-1855">
              <thead data-id="element-1856">
                <tr className="bg-slate-50/50 border-b border-slate-100 text-xs uppercase tracking-wider text-slate-500 font-semibold" data-id="element-1857">
                  {cols.filter(c => visibleColumns.includes(c.key)).map(col => <th key={col.key} className="px-4 py-4" data-id="element-1858">
                        {col.label}
                      </th>)}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-1859">
                {paginatedData.map(item => <tr key={item.id} className="hover:bg-slate-50/80 transition-colors group" data-id="element-1860">
                    {visibleColumns.includes('number') && <td className="px-4 py-4 font-mono text-xs text-slate-500" data-id="element-1861">
                        {item.number}
                      </td>}
                    {visibleColumns.includes('description') && <td className="px-4 py-4 text-sm font-medium text-slate-900" data-id="element-1862">
                        {item.description}
                      </td>}
                    {visibleColumns.includes('amount') && <td className="px-4 py-4 text-sm font-medium text-slate-900" data-id="element-1863">
                        {item.amount}
                      </td>}
                    {visibleColumns.includes('status') && <td className="px-4 py-4" data-id="element-1864">
                        <Badge variant={item.status === 'Pagata' ? 'success' : item.status === 'In attesa' ? 'warning' : item.status === 'Scaduta' ? 'danger' : 'neutral'} data-id="element-1865">
                          {item.status}
                        </Badge>
                      </td>}
                    {visibleColumns.includes('dueDate') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-1866">
                        {item.dueDate}
                      </td>}
                    {visibleColumns.includes('actions') && <td className="px-4 py-4 pr-6" data-id="element-1867">
                        {actionButtons(item, 'fattura')}
                      </td>}
                  </tr>)}
              </tbody>
            </table>
          </div>
          <div className="border-t border-slate-100" data-id="element-1868">
            <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={goToPage} totalItems={totalItems} pageSize={pageSize} onPageSizeChange={setPageSize} data-id="element-1869" />
          </div>
        </Card>

        <Modal isOpen={isViewOpen} onClose={() => setIsViewOpen(false)} title="Dettaglio fattura" size="lg" data-id="element-1870">
          {selectedItem && <div className="space-y-6" data-id="element-1871">
              <div className="grid grid-cols-2 gap-4" data-id="element-1872">
                <div data-id="element-1873">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-1874">
                    Numero
                  </span>
                  <p className="font-mono font-medium mt-1" data-id="element-1875">
                    {selectedItem.number}
                  </p>
                </div>
                <div data-id="element-1876">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-1877">
                    Stato
                  </span>
                  <div className="mt-1" data-id="element-1878">
                    <Badge variant={selectedItem.status === 'Pagata' ? 'success' : selectedItem.status === 'In attesa' ? 'warning' : 'danger'} data-id="element-1879">
                      {selectedItem.status}
                    </Badge>
                  </div>
                </div>
                <div className="col-span-full" data-id="element-1880">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-1881">
                    Descrizione
                  </span>
                  <p className="font-medium mt-1" data-id="element-1882">{selectedItem.description}</p>
                </div>
                <div data-id="element-1883">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-1884">
                    Importo
                  </span>
                  <p className="font-medium mt-1" data-id="element-1885">{selectedItem.amount}</p>
                </div>
                <div data-id="element-1886">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-1887">
                    Scadenza
                  </span>
                  <p className="font-medium mt-1" data-id="element-1888">{selectedItem.dueDate}</p>
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-1889">
                <Button variant="flat" color="default" onClick={() => setIsViewOpen(false)} data-id="element-1890">
                  Chiudi
                </Button>
                <Button onClick={() => {
              setIsViewOpen(false);
              handleEdit(selectedItem);
            }} startContent={<Edit className="h-4 w-4" data-id="element-1892" />} data-id="element-1891">
                  Modifica
                </Button>
              </div>
            </div>}
        </Modal>

        <Modal isOpen={isEditOpen} onClose={() => setIsEditOpen(false)} title={selectedItem ? 'Modifica fattura' : 'Nuova fattura'} size="lg" data-id="element-1893">
          <AnimatePresence mode="wait" data-id="element-1894">
            {saveSuccess ? <SaveSuccessView label="Fattura salvata con successo" data-id="element-1895" /> : <motion.div key="form" initial={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} data-id="element-1896">
                <div className="space-y-6" data-id="element-1897">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1898">
                    <Input label="Numero" value={formData.number || ''} onChange={e => setFormData({
                  ...formData,
                  number: e.target.value
                })} data-id="element-1899" />
                    <Input label="Importo" value={formData.amount || ''} onChange={e => setFormData({
                  ...formData,
                  amount: e.target.value
                })} data-id="element-1900" />
                    <Input label="Descrizione" value={formData.description || ''} onChange={e => setFormData({
                  ...formData,
                  description: e.target.value
                })} required className="col-span-full" data-id="element-1901" />
                    <Select label="Stato" value={formData.status || ''} onChange={e => setFormData({
                  ...formData,
                  status: e.target.value
                })} options={[{
                  value: 'Bozza',
                  label: 'Bozza'
                }, {
                  value: 'In attesa',
                  label: 'In attesa'
                }, {
                  value: 'Pagata',
                  label: 'Pagata'
                }, {
                  value: 'Scaduta',
                  label: 'Scaduta'
                }]} required data-id="element-1902" />
                    <Input label="Data scadenza" type="date" value={formData.dueDate || ''} onChange={e => setFormData({
                  ...formData,
                  dueDate: e.target.value
                })} data-id="element-1903" />
                  </div>
                  <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-1904">
                    <Button variant="flat" color="default" onClick={() => setIsEditOpen(false)} data-id="element-1905">
                      Annulla
                    </Button>
                    <Button onClick={handleSave} startContent={<Save className="h-4 w-4" data-id="element-1907" />} data-id="element-1906">
                      Salva fattura
                    </Button>
                  </div>
                </div>
              </motion.div>}
          </AnimatePresence>
        </Modal>

        <DeleteModal isOpen={isDeleteOpen} onClose={() => setIsDeleteOpen(false)} onConfirm={confirmDelete} itemLabel={selectedItem?.number || ''} deleteSuccess={deleteSuccess} data-id="element-1908" />
      </Layout>;
  }
  // ─── Training Section ─────────────────────────────────────────────────────
  if (sectionId === 'training') {
    return <Layout data-id="element-1909">
        {header}
        {toolbar}
        <Card noPadding className="overflow-hidden" data-id="element-1910">
          <div className="overflow-x-auto" data-id="element-1911">
            <table className="w-full text-left border-collapse table-auto" data-id="element-1912">
              <thead data-id="element-1913">
                <tr className="bg-slate-50/50 border-b border-slate-100 text-xs uppercase tracking-wider text-slate-500 font-semibold" data-id="element-1914">
                  {cols.filter(c => visibleColumns.includes(c.key)).map(col => <th key={col.key} className="px-4 py-4" data-id="element-1915">
                        {col.label}
                      </th>)}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-1916">
                {paginatedData.map(item => <tr key={item.id} className="hover:bg-slate-50/80 transition-colors group" data-id="element-1917">
                    {visibleColumns.includes('code') && <td className="px-4 py-4 font-mono text-xs text-slate-500" data-id="element-1918">
                        {item.code}
                      </td>}
                    {visibleColumns.includes('title') && <td className="px-4 py-4 text-sm font-medium text-slate-900" data-id="element-1919">
                        {item.title}
                      </td>}
                    {visibleColumns.includes('type') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-1920">
                        {item.type}
                      </td>}
                    {visibleColumns.includes('mode') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-1921">
                        {item.mode}
                      </td>}
                    {visibleColumns.includes('date') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-1922">
                        {item.date}
                      </td>}
                    {visibleColumns.includes('enrolled') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-1923">
                        {item.enrolled}
                      </td>}
                    {visibleColumns.includes('status') && <td className="px-4 py-4" data-id="element-1924">
                        <Badge variant={item.status === 'In corso' ? 'success' : item.status === 'Programmato' ? 'info' : 'neutral'} data-id="element-1925">
                          {item.status}
                        </Badge>
                      </td>}
                    {visibleColumns.includes('actions') && <td className="px-4 py-4 pr-6" data-id="element-1926">
                        {actionButtons(item, 'corso')}
                      </td>}
                  </tr>)}
              </tbody>
            </table>
          </div>
          <div className="border-t border-slate-100" data-id="element-1927">
            <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={goToPage} totalItems={totalItems} pageSize={pageSize} onPageSizeChange={setPageSize} data-id="element-1928" />
          </div>
        </Card>

        <Modal isOpen={isViewOpen} onClose={() => setIsViewOpen(false)} title="Dettaglio corso" size="lg" data-id="element-1929">
          {selectedItem && <div className="space-y-6" data-id="element-1930">
              <div className="grid grid-cols-2 gap-4" data-id="element-1931">
                <div data-id="element-1932">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-1933">
                    Codice
                  </span>
                  <p className="font-mono font-medium mt-1" data-id="element-1934">
                    {selectedItem.code}
                  </p>
                </div>
                <div data-id="element-1935">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-1936">
                    Stato
                  </span>
                  <div className="mt-1" data-id="element-1937">
                    <Badge variant={selectedItem.status === 'In corso' ? 'success' : selectedItem.status === 'Programmato' ? 'info' : 'neutral'} data-id="element-1938">
                      {selectedItem.status}
                    </Badge>
                  </div>
                </div>
                <div className="col-span-full" data-id="element-1939">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-1940">
                    Titolo
                  </span>
                  <p className="font-medium text-lg mt-1" data-id="element-1941">
                    {selectedItem.title}
                  </p>
                </div>
                <div data-id="element-1942">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-1943">
                    Tipologia
                  </span>
                  <p className="font-medium mt-1" data-id="element-1944">{selectedItem.type}</p>
                </div>
                <div data-id="element-1945">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-1946">
                    Modalità
                  </span>
                  <p className="font-medium mt-1" data-id="element-1947">{selectedItem.mode}</p>
                </div>
                <div data-id="element-1948">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-1949">
                    Data
                  </span>
                  <p className="font-medium mt-1" data-id="element-1950">{selectedItem.date}</p>
                </div>
                <div data-id="element-1951">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-1952">
                    Iscritti
                  </span>
                  <p className="font-medium mt-1" data-id="element-1953">{selectedItem.enrolled}</p>
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-1954">
                <Button variant="flat" color="default" onClick={() => setIsViewOpen(false)} data-id="element-1955">
                  Chiudi
                </Button>
                <Button onClick={() => {
              setIsViewOpen(false);
              handleEdit(selectedItem);
            }} startContent={<Edit className="h-4 w-4" data-id="element-1957" />} data-id="element-1956">
                  Modifica
                </Button>
              </div>
            </div>}
        </Modal>

        <Modal isOpen={isEditOpen} onClose={() => setIsEditOpen(false)} title={selectedItem ? 'Modifica corso' : 'Nuovo corso'} size="lg" data-id="element-1958">
          <AnimatePresence mode="wait" data-id="element-1959">
            {saveSuccess ? <SaveSuccessView label="Corso salvato con successo" data-id="element-1960" /> : <motion.div key="form" initial={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} data-id="element-1961">
                <div className="space-y-6" data-id="element-1962">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1963">
                    <Input label="Codice" value={formData.code || ''} onChange={e => setFormData({
                  ...formData,
                  code: e.target.value
                })} disabled={!!selectedItem} data-id="element-1964" />
                    <Select label="Stato" value={formData.status || ''} onChange={e => setFormData({
                  ...formData,
                  status: e.target.value
                })} options={[{
                  value: 'Programmato',
                  label: 'Programmato'
                }, {
                  value: 'In corso',
                  label: 'In corso'
                }, {
                  value: 'Completato',
                  label: 'Completato'
                }]} required data-id="element-1965" />
                    <Input label="Titolo" value={formData.title || ''} onChange={e => setFormData({
                  ...formData,
                  title: e.target.value
                })} required className="col-span-full" data-id="element-1966" />
                    <Select label="Tipologia" value={formData.type || ''} onChange={e => setFormData({
                  ...formData,
                  type: e.target.value
                })} options={[{
                  value: 'Sicurezza',
                  label: 'Sicurezza'
                }, {
                  value: 'HACCP',
                  label: 'HACCP'
                }, {
                  value: 'Privacy',
                  label: 'Privacy'
                }]} required data-id="element-1967" />
                    <Select label="Modalità" value={formData.mode || ''} onChange={e => setFormData({
                  ...formData,
                  mode: e.target.value
                })} options={[{
                  value: 'Sede SEF',
                  label: 'Sede SEF'
                }, {
                  value: 'Sede cliente',
                  label: 'Sede cliente'
                }, {
                  value: 'A distanza',
                  label: 'A distanza'
                }]} required data-id="element-1968" />
                    <DatePicker label="Data" value={formData.date || ''} onChange={e => setFormData({
                  ...formData,
                  date: e.target.value
                })} data-id="element-1969" />
                  </div>
                  <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-1970">
                    <Button variant="flat" color="default" onClick={() => setIsEditOpen(false)} data-id="element-1971">
                      Annulla
                    </Button>
                    <Button onClick={handleSave} startContent={<Save className="h-4 w-4" data-id="element-1973" />} data-id="element-1972">
                      Salva corso
                    </Button>
                  </div>
                </div>
              </motion.div>}
          </AnimatePresence>
        </Modal>

        <DeleteModal isOpen={isDeleteOpen} onClose={() => setIsDeleteOpen(false)} onConfirm={confirmDelete} itemLabel={selectedItem?.title || ''} deleteSuccess={deleteSuccess} data-id="element-1974" />
      </Layout>;
  }
  // ─── Operations Section ───────────────────────────────────────────────────
  if (sectionId === 'operations') {
    return <Layout data-id="element-1975">
        {header}
        {toolbar}
        <Card noPadding className="overflow-hidden" data-id="element-1976">
          <div className="overflow-x-auto" data-id="element-1977">
            <table className="w-full text-left border-collapse table-auto" data-id="element-1978">
              <thead data-id="element-1979">
                <tr className="bg-slate-50/50 border-b border-slate-100 text-xs uppercase tracking-wider text-slate-500 font-semibold" data-id="element-1980">
                  {cols.filter(c => visibleColumns.includes(c.key)).map(col => <th key={col.key} className="px-4 py-4" data-id="element-1981">
                        {col.label}
                      </th>)}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-1982">
                {paginatedData.map(item => <tr key={item.id} className="hover:bg-slate-50/80 transition-colors group" data-id="element-1983">
                    {visibleColumns.includes('code') && <td className="px-4 py-4 font-mono text-xs text-slate-500" data-id="element-1984">
                        {item.code}
                      </td>}
                    {visibleColumns.includes('activityType') && <td className="px-4 py-4 text-sm font-medium text-slate-900" data-id="element-1985">
                        {item.activityType}
                      </td>}
                    {visibleColumns.includes('ambito') && <td className="px-4 py-4" data-id="element-1986">
                        <Badge variant="neutral" className="bg-slate-100 text-slate-600 border-slate-200" data-id="element-1987">
                          {item.ambito}
                        </Badge>
                      </td>}
                    {visibleColumns.includes('contract') && <td className="px-4 py-4 font-mono text-xs text-slate-500" data-id="element-1988">
                        {item.contract}
                      </td>}
                    {visibleColumns.includes('operator') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-1989">
                        {item.operator}
                      </td>}
                    {visibleColumns.includes('status') && <td className="px-4 py-4" data-id="element-1990">
                        <Badge variant={statusColors[item.status] as any || 'neutral'} data-id="element-1991">
                          {statusLabels[item.status] || item.status}
                        </Badge>
                      </td>}
                    {visibleColumns.includes('deadline') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-1992">
                        {item.deadline}
                      </td>}
                    {visibleColumns.includes('actions') && <td className="px-4 py-4 pr-6" data-id="element-1993">
                        {actionButtons(item, 'attività')}
                      </td>}
                  </tr>)}
              </tbody>
            </table>
          </div>
          <div className="border-t border-slate-100" data-id="element-1994">
            <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={goToPage} totalItems={totalItems} pageSize={pageSize} onPageSizeChange={setPageSize} data-id="element-1995" />
          </div>
        </Card>

        <Modal isOpen={isViewOpen} onClose={() => setIsViewOpen(false)} title={selectedItem ? `Dettaglio attività — ${selectedItem.code}` : ''} size="lg" data-id="element-1996">
          {selectedItem && <div className="space-y-6" data-id="element-1997">
              <div className="grid grid-cols-2 gap-4" data-id="element-1998">
                <div data-id="element-1999">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2000">
                    Codice
                  </span>
                  <p className="font-mono font-medium mt-1" data-id="element-2001">
                    {selectedItem.code}
                  </p>
                </div>
                <div data-id="element-2002">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2003">
                    Stato
                  </span>
                  <div className="mt-1" data-id="element-2004">
                    <Badge variant={statusColors[selectedItem.status] as any || 'neutral'} data-id="element-2005">
                      {statusLabels[selectedItem.status] || selectedItem.status}
                    </Badge>
                  </div>
                </div>
                <div className="col-span-full" data-id="element-2006">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2007">
                    Tipologia attività
                  </span>
                  <p className="font-medium mt-1" data-id="element-2008">
                    {selectedItem.activityType}
                  </p>
                </div>
                <div data-id="element-2009">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2010">
                    Ambito
                  </span>
                  <p className="font-medium mt-1" data-id="element-2011">{selectedItem.ambito}</p>
                </div>
                <div data-id="element-2012">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2013">
                    Contratto
                  </span>
                  <p className="font-mono font-medium mt-1" data-id="element-2014">
                    {selectedItem.contract}
                  </p>
                </div>
                <div data-id="element-2015">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2016">
                    Operatore
                  </span>
                  <p className="font-medium mt-1" data-id="element-2017">{selectedItem.operator}</p>
                </div>
                <div data-id="element-2018">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2019">
                    Zona
                  </span>
                  <p className="font-medium mt-1" data-id="element-2020">{selectedItem.zone}</p>
                </div>
                <div data-id="element-2021">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2022">
                    Scadenza
                  </span>
                  <p className="font-medium mt-1" data-id="element-2023">{selectedItem.deadline}</p>
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-2024">
                <Button variant="flat" color="default" onClick={() => setIsViewOpen(false)} data-id="element-2025">
                  Chiudi
                </Button>
                <Button onClick={() => {
              setIsViewOpen(false);
              handleEdit(selectedItem);
            }} startContent={<Edit className="h-4 w-4" data-id="element-2027" />} data-id="element-2026">
                  Modifica
                </Button>
              </div>
            </div>}
        </Modal>

        <Modal isOpen={isEditOpen} onClose={() => setIsEditOpen(false)} title={selectedItem ? 'Modifica attività' : 'Nuova attività'} size="lg" data-id="element-2028">
          <AnimatePresence mode="wait" data-id="element-2029">
            {saveSuccess ? <SaveSuccessView label="Attività salvata con successo" data-id="element-2030" /> : <motion.div key="form" initial={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} data-id="element-2031">
                <div className="space-y-6" data-id="element-2032">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2033">
                    <Input label="Codice" value={formData.code || ''} onChange={e => setFormData({
                  ...formData,
                  code: e.target.value
                })} data-id="element-2034" />
                    <Select label="Tipologia attività" value={formData.activityType || ''} onChange={e => setFormData({
                  ...formData,
                  activityType: e.target.value
                })} options={['Sopralluogo periodico', 'Sopralluogo iniziale', 'Backoffice', 'Riunione in ufficio', 'Consulenza via email'].map(t => ({
                  value: t,
                  label: t
                }))} required data-id="element-2035" />
                    <Select label="Ambito" value={formData.ambito || ''} onChange={e => setFormData({
                  ...formData,
                  ambito: e.target.value
                })} options={['Sicurezza', 'Privacy', 'HACCP', 'Ambiente', 'Formazione'].map(a => ({
                  value: a,
                  label: a
                }))} required data-id="element-2036" />
                    <Select label="Operatore" value={formData.operator || ''} onChange={e => setFormData({
                  ...formData,
                  operator: e.target.value
                })} options={[{
                  value: 'Alessio',
                  label: 'Alessio'
                }, {
                  value: 'Lorenzo',
                  label: 'Lorenzo'
                }, {
                  value: 'Gian Franco',
                  label: 'Gian Franco'
                }, {
                  value: 'Tiziano',
                  label: 'Tiziano'
                }]} required data-id="element-2037" />
                    <Select label="Stato" value={formData.status || ''} onChange={e => setFormData({
                  ...formData,
                  status: e.target.value
                })} options={Object.entries(statusLabels).map(([v, l]) => ({
                  value: v,
                  label: l
                }))} required data-id="element-2038" />
                    <DatePicker label="Scadenza" value={formData.deadline || ''} onChange={e => setFormData({
                  ...formData,
                  deadline: e.target.value
                })} data-id="element-2039" />
                  </div>
                  <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-2040">
                    <Button variant="flat" color="default" onClick={() => setIsEditOpen(false)} data-id="element-2041">
                      Annulla
                    </Button>
                    <Button onClick={handleSave} startContent={<Save className="h-4 w-4" data-id="element-2043" />} data-id="element-2042">
                      Salva attività
                    </Button>
                  </div>
                </div>
              </motion.div>}
          </AnimatePresence>
        </Modal>

        <DeleteModal isOpen={isDeleteOpen} onClose={() => setIsDeleteOpen(false)} onConfirm={confirmDelete} itemLabel={selectedItem?.code || ''} deleteSuccess={deleteSuccess} data-id="element-2044" />
      </Layout>;
  }
  // ─── Non Conformities Section ─────────────────────────────────────────────
  if (sectionId === 'non_conformities') {
    return <Layout data-id="element-2045">
        {header}
        {toolbar}
        <Card noPadding className="overflow-hidden" data-id="element-2046">
          <div className="overflow-x-auto" data-id="element-2047">
            <table className="w-full text-left border-collapse table-auto" data-id="element-2048">
              <thead data-id="element-2049">
                <tr className="bg-slate-50/50 border-b border-slate-100 text-xs uppercase tracking-wider text-slate-500 font-semibold" data-id="element-2050">
                  {cols.filter(c => visibleColumns.includes(c.key)).map(col => <th key={col.key} className="px-4 py-4" data-id="element-2051">
                        {col.label}
                      </th>)}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-2052">
                {paginatedData.map(item => <tr key={item.id} className="hover:bg-slate-50/80 transition-colors group" data-id="element-2053">
                    {visibleColumns.includes('number') && <td className="px-4 py-4 font-mono text-xs text-slate-500" data-id="element-2054">
                        {item.number}
                      </td>}
                    {visibleColumns.includes('type') && <td className="px-4 py-4" data-id="element-2055">
                        <Badge variant={item.type === 'Non conformità maggiore' ? 'danger' : item.type === 'Non conformità minore' ? 'warning' : 'info'} data-id="element-2056">
                          {item.type}
                        </Badge>
                      </td>}
                    {visibleColumns.includes('origin') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-2057">
                        {item.origin}
                      </td>}
                    {visibleColumns.includes('description') && <td className="px-4 py-4 text-sm text-slate-600 max-w-xs truncate" data-id="element-2058">
                        {item.description}
                      </td>}
                    {visibleColumns.includes('date') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-2059">
                        {item.date}
                      </td>}
                    {visibleColumns.includes('responsible') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-2060">
                        {item.responsible}
                      </td>}
                    {visibleColumns.includes('status') && <td className="px-4 py-4" data-id="element-2061">
                        <Badge variant={item.status === 'Aperta' ? 'danger' : item.status === 'Sospesa' ? 'warning' : 'success'} data-id="element-2062">
                          {item.status}
                        </Badge>
                      </td>}
                    {visibleColumns.includes('actions') && <td className="px-4 py-4 pr-6" data-id="element-2063">
                        <div className="flex items-center gap-1" data-id="element-2064">
                          <Tooltip content="Visualizza NC" data-id="element-2065">
                            <Button variant="light" size="sm" className="!px-1" onClick={() => handleView(item)} data-id="element-2066">
                              <Eye className="h-4 w-4 text-slate-500" data-id="element-2067" />
                            </Button>
                          </Tooltip>
                          <Tooltip content="Invia report" data-id="element-2068">
                            <Button variant="light" size="sm" className="!px-1" data-id="element-2069">
                              <Mail className="h-4 w-4 text-blue-500" data-id="element-2070" />
                            </Button>
                          </Tooltip>
                          <Tooltip content="Modifica NC" data-id="element-2071">
                            <Button variant="light" size="sm" className="!px-1" onClick={() => handleEdit(item)} data-id="element-2072">
                              <Edit className="h-4 w-4 text-seform-orange" data-id="element-2073" />
                            </Button>
                          </Tooltip>
                          <Tooltip content="Elimina NC" data-id="element-2074">
                            <Button variant="light" size="sm" className="!px-1" onClick={() => handleDelete(item)} data-id="element-2075">
                              <Trash2 className="h-4 w-4 text-red-500" data-id="element-2076" />
                            </Button>
                          </Tooltip>
                        </div>
                      </td>}
                  </tr>)}
              </tbody>
            </table>
          </div>
          <div className="border-t border-slate-100" data-id="element-2077">
            <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={goToPage} totalItems={totalItems} pageSize={pageSize} onPageSizeChange={setPageSize} data-id="element-2078" />
          </div>
        </Card>

        <Modal isOpen={isViewOpen} onClose={() => setIsViewOpen(false)} title={selectedItem ? `Dettaglio NC — ${selectedItem.number}` : ''} size="lg" data-id="element-2079">
          {selectedItem && <div className="space-y-6" data-id="element-2080">
              <div className="grid grid-cols-2 gap-4" data-id="element-2081">
                <div data-id="element-2082">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2083">
                    Codice
                  </span>
                  <p className="font-mono font-medium mt-1" data-id="element-2084">
                    {selectedItem.number}
                  </p>
                </div>
                <div data-id="element-2085">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2086">
                    Stato
                  </span>
                  <div className="mt-1" data-id="element-2087">
                    <Badge variant={selectedItem.status === 'Aperta' ? 'danger' : selectedItem.status === 'Sospesa' ? 'warning' : 'success'} data-id="element-2088">
                      {selectedItem.status}
                    </Badge>
                  </div>
                </div>
                <div data-id="element-2089">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2090">
                    Tipologia
                  </span>
                  <div className="mt-1" data-id="element-2091">
                    <Badge variant={selectedItem.type === 'Non conformità maggiore' ? 'danger' : 'warning'} data-id="element-2092">
                      {selectedItem.type}
                    </Badge>
                  </div>
                </div>
                <div data-id="element-2093">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2094">
                    Origine
                  </span>
                  <p className="font-medium mt-1" data-id="element-2095">{selectedItem.origin}</p>
                </div>
                <div className="col-span-full" data-id="element-2096">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2097">
                    Descrizione
                  </span>
                  <p className="text-sm text-slate-700 bg-slate-50 rounded-lg p-3 border border-slate-100 mt-1" data-id="element-2098">
                    {selectedItem.description}
                  </p>
                </div>
                <div data-id="element-2099">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2100">
                    Data rilevazione
                  </span>
                  <p className="font-medium mt-1" data-id="element-2101">{selectedItem.date}</p>
                </div>
                <div data-id="element-2102">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2103">
                    Responsabile
                  </span>
                  <p className="font-medium mt-1" data-id="element-2104">{selectedItem.responsible}</p>
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-2105">
                <Button variant="flat" color="default" onClick={() => setIsViewOpen(false)} data-id="element-2106">
                  Chiudi
                </Button>
                <Button onClick={() => {
              setIsViewOpen(false);
              handleEdit(selectedItem);
            }} startContent={<Edit className="h-4 w-4" data-id="element-2108" />} data-id="element-2107">
                  Modifica
                </Button>
              </div>
            </div>}
        </Modal>

        <Modal isOpen={isEditOpen} onClose={() => setIsEditOpen(false)} title={selectedItem ? 'Modifica non conformità' : 'Nuova non conformità'} size="lg" data-id="element-2109">
          <AnimatePresence mode="wait" data-id="element-2110">
            {saveSuccess ? <SaveSuccessView label="Non conformità salvata con successo" data-id="element-2111" /> : <motion.div key="form" initial={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} data-id="element-2112">
                <div className="space-y-6" data-id="element-2113">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2114">
                    <Input label="Codice" value={formData.number || ''} onChange={e => setFormData({
                  ...formData,
                  number: e.target.value
                })} disabled={!!selectedItem} data-id="element-2115" />
                    <Select label="Tipologia" value={formData.type || ''} onChange={e => setFormData({
                  ...formData,
                  type: e.target.value
                })} options={[{
                  value: 'Non conformità maggiore',
                  label: 'Non conformità maggiore'
                }, {
                  value: 'Non conformità minore',
                  label: 'Non conformità minore'
                }, {
                  value: 'Osservazione',
                  label: 'Osservazione'
                }]} required data-id="element-2116" />
                    <Select label="Origine" value={formData.origin || ''} onChange={e => setFormData({
                  ...formData,
                  origin: e.target.value
                })} options={[{
                  value: 'Interna',
                  label: 'Interna'
                }, {
                  value: 'Cliente (reclamo)',
                  label: 'Cliente (reclamo)'
                }, {
                  value: 'Fornitore',
                  label: 'Fornitore'
                }]} required data-id="element-2117" />
                    <DatePicker label="Data rilevazione" value={formData.date || ''} onChange={e => setFormData({
                  ...formData,
                  date: e.target.value
                })} data-id="element-2118" />
                    <Input label="Responsabile" value={formData.responsible || ''} onChange={e => setFormData({
                  ...formData,
                  responsible: e.target.value
                })} data-id="element-2119" />
                    <Select label="Stato" value={formData.status || ''} onChange={e => setFormData({
                  ...formData,
                  status: e.target.value
                })} options={[{
                  value: 'Aperta',
                  label: 'Aperta'
                }, {
                  value: 'Sospesa',
                  label: 'Sospesa'
                }, {
                  value: 'Chiusa',
                  label: 'Chiusa'
                }]} required data-id="element-2120" />
                    <div className="col-span-full" data-id="element-2121">
                      <Textarea label="Descrizione" value={formData.description || ''} onChange={e => setFormData({
                    ...formData,
                    description: e.target.value
                  })} required data-id="element-2122" />
                    </div>
                  </div>
                  <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-2123">
                    <Button variant="flat" color="default" onClick={() => setIsEditOpen(false)} data-id="element-2124">
                      Annulla
                    </Button>
                    <Button onClick={handleSave} startContent={<Save className="h-4 w-4" data-id="element-2126" />} data-id="element-2125">
                      Salva
                    </Button>
                  </div>
                </div>
              </motion.div>}
          </AnimatePresence>
        </Modal>

        <DeleteModal isOpen={isDeleteOpen} onClose={() => setIsDeleteOpen(false)} onConfirm={confirmDelete} itemLabel={selectedItem?.number || ''} deleteSuccess={deleteSuccess} data-id="element-2127" />
      </Layout>;
  }
  // ─── Improvements Section ─────────────────────────────────────────────────
  if (sectionId === 'improvements') {
    return <Layout data-id="element-2128">
        {header}
        {toolbar}
        <Card noPadding className="overflow-hidden" data-id="element-2129">
          <div className="overflow-x-auto" data-id="element-2130">
            <table className="w-full text-left border-collapse table-auto" data-id="element-2131">
              <thead data-id="element-2132">
                <tr className="bg-slate-50/50 border-b border-slate-100 text-xs uppercase tracking-wider text-slate-500 font-semibold" data-id="element-2133">
                  {cols.filter(c => visibleColumns.includes(c.key)).map(col => <th key={col.key} className="px-4 py-4" data-id="element-2134">
                        {col.label}
                      </th>)}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-2135">
                {paginatedData.map(item => <tr key={item.id} className="hover:bg-slate-50/80 transition-colors group" data-id="element-2136">
                    {visibleColumns.includes('number') && <td className="px-4 py-4 font-mono text-xs text-slate-500" data-id="element-2137">
                        {item.number}
                      </td>}
                    {visibleColumns.includes('subject') && <td className="px-4 py-4 text-sm font-medium text-slate-900" data-id="element-2138">
                        {item.subject}
                      </td>}
                    {visibleColumns.includes('description') && <td className="px-4 py-4 text-sm text-slate-600 max-w-xs truncate" data-id="element-2139">
                        {item.description}
                      </td>}
                    {visibleColumns.includes('date') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-2140">
                        {item.date}
                      </td>}
                    {visibleColumns.includes('responsible') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-2141">
                        {item.responsible}
                      </td>}
                    {visibleColumns.includes('status') && <td className="px-4 py-4" data-id="element-2142">
                        <Badge variant={item.status === 'Approvato' ? 'success' : item.status === 'Sospesa' ? 'warning' : 'info'} data-id="element-2143">
                          {item.status}
                        </Badge>
                      </td>}
                    {visibleColumns.includes('actions') && <td className="px-4 py-4 pr-6" data-id="element-2144">
                        {actionButtons(item, 'miglioramento')}
                      </td>}
                  </tr>)}
              </tbody>
            </table>
          </div>
          <div className="border-t border-slate-100" data-id="element-2145">
            <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={goToPage} totalItems={totalItems} pageSize={pageSize} onPageSizeChange={setPageSize} data-id="element-2146" />
          </div>
        </Card>

        <Modal isOpen={isViewOpen} onClose={() => setIsViewOpen(false)} title={selectedItem ? `Dettaglio miglioramento — ${selectedItem.number}` : ''} size="lg" data-id="element-2147">
          {selectedItem && <div className="space-y-6" data-id="element-2148">
              <div className="grid grid-cols-2 gap-4" data-id="element-2149">
                <div data-id="element-2150">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2151">
                    Codice
                  </span>
                  <p className="font-mono font-medium mt-1" data-id="element-2152">
                    {selectedItem.number}
                  </p>
                </div>
                <div data-id="element-2153">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2154">
                    Stato
                  </span>
                  <div className="mt-1" data-id="element-2155">
                    <Badge variant={selectedItem.status === 'Approvato' ? 'success' : selectedItem.status === 'Sospesa' ? 'warning' : 'info'} data-id="element-2156">
                      {selectedItem.status}
                    </Badge>
                  </div>
                </div>
                <div className="col-span-full" data-id="element-2157">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2158">
                    Oggetto
                  </span>
                  <p className="font-medium mt-1" data-id="element-2159">{selectedItem.subject}</p>
                </div>
                <div className="col-span-full" data-id="element-2160">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2161">
                    Descrizione
                  </span>
                  <p className="text-sm text-slate-700 bg-slate-50 rounded-lg p-3 border border-slate-100 mt-1" data-id="element-2162">
                    {selectedItem.description}
                  </p>
                </div>
                <div data-id="element-2163">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2164">
                    Data proposta
                  </span>
                  <p className="font-medium mt-1" data-id="element-2165">{selectedItem.date}</p>
                </div>
                <div data-id="element-2166">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2167">
                    Responsabile
                  </span>
                  <p className="font-medium mt-1" data-id="element-2168">{selectedItem.responsible}</p>
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-2169">
                <Button variant="flat" color="default" onClick={() => setIsViewOpen(false)} data-id="element-2170">
                  Chiudi
                </Button>
                <Button onClick={() => {
              setIsViewOpen(false);
              handleEdit(selectedItem);
            }} startContent={<Edit className="h-4 w-4" data-id="element-2172" />} data-id="element-2171">
                  Modifica
                </Button>
              </div>
            </div>}
        </Modal>

        <Modal isOpen={isEditOpen} onClose={() => setIsEditOpen(false)} title={selectedItem ? 'Modifica miglioramento' : 'Nuovo miglioramento'} size="lg" data-id="element-2173">
          <AnimatePresence mode="wait" data-id="element-2174">
            {saveSuccess ? <SaveSuccessView label="Miglioramento salvato con successo" data-id="element-2175" /> : <motion.div key="form" initial={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} data-id="element-2176">
                <div className="space-y-6" data-id="element-2177">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2178">
                    <Input label="Codice" value={formData.number || ''} onChange={e => setFormData({
                  ...formData,
                  number: e.target.value
                })} disabled={!!selectedItem} data-id="element-2179" />
                    <DatePicker label="Data proposta" value={formData.date || ''} onChange={e => setFormData({
                  ...formData,
                  date: e.target.value
                })} data-id="element-2180" />
                    <div className="col-span-full" data-id="element-2181">
                      <Input label="Oggetto" value={formData.subject || ''} onChange={e => setFormData({
                    ...formData,
                    subject: e.target.value
                  })} required data-id="element-2182" />
                    </div>
                    <div className="col-span-full" data-id="element-2183">
                      <Textarea label="Descrizione" value={formData.description || ''} onChange={e => setFormData({
                    ...formData,
                    description: e.target.value
                  })} required data-id="element-2184" />
                    </div>
                    <Input label="Responsabile" value={formData.responsible || ''} onChange={e => setFormData({
                  ...formData,
                  responsible: e.target.value
                })} data-id="element-2185" />
                    <Select label="Stato" value={formData.status || ''} onChange={e => setFormData({
                  ...formData,
                  status: e.target.value
                })} options={[{
                  value: 'In valutazione',
                  label: 'In valutazione'
                }, {
                  value: 'Approvato',
                  label: 'Approvato'
                }, {
                  value: 'Sospesa',
                  label: 'Sospesa'
                }, {
                  value: 'Scartata',
                  label: 'Scartata'
                }]} required data-id="element-2186" />
                  </div>
                  <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-2187">
                    <Button variant="flat" color="default" onClick={() => setIsEditOpen(false)} data-id="element-2188">
                      Annulla
                    </Button>
                    <Button onClick={handleSave} startContent={<Save className="h-4 w-4" data-id="element-2190" />} data-id="element-2189">
                      Salva
                    </Button>
                  </div>
                </div>
              </motion.div>}
          </AnimatePresence>
        </Modal>

        <DeleteModal isOpen={isDeleteOpen} onClose={() => setIsDeleteOpen(false)} onConfirm={confirmDelete} itemLabel={selectedItem?.number || ''} deleteSuccess={deleteSuccess} data-id="element-2191" />
      </Layout>;
  }
  // ─── Equipment Section ────────────────────────────────────────────────────
  if (sectionId === 'equipment') {
    return <Layout data-id="element-2192">
        {header}
        {toolbar}
        <Card noPadding className="overflow-hidden" data-id="element-2193">
          <div className="overflow-x-auto" data-id="element-2194">
            <table className="w-full text-left border-collapse table-auto" data-id="element-2195">
              <thead data-id="element-2196">
                <tr className="bg-slate-50/50 border-b border-slate-100 text-xs uppercase tracking-wider text-slate-500 font-semibold" data-id="element-2197">
                  {cols.filter(c => visibleColumns.includes(c.key)).map(col => <th key={col.key} className="px-4 py-4" data-id="element-2198">
                        {col.label}
                      </th>)}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-2199">
                {paginatedData.map(item => <tr key={item.id} className="hover:bg-slate-50/80 transition-colors group" data-id="element-2200">
                    {visibleColumns.includes('code') && <td className="px-4 py-4 font-mono text-xs text-slate-500" data-id="element-2201">
                        {item.code}
                      </td>}
                    {visibleColumns.includes('name') && <td className="px-4 py-4 text-sm font-medium text-slate-900" data-id="element-2202">
                        {item.name}
                      </td>}
                    {visibleColumns.includes('type') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-2203">
                        {item.type}
                      </td>}
                    {visibleColumns.includes('lastMaintenance') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-2204">
                        {item.lastMaintenance}
                      </td>}
                    {visibleColumns.includes('nextMaintenance') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-2205">
                        {item.nextMaintenance}
                      </td>}
                    {visibleColumns.includes('responsible') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-2206">
                        {item.responsible}
                      </td>}
                    {visibleColumns.includes('maintenanceType') && <td className="px-4 py-4" data-id="element-2207">
                        <Badge variant={item.maintenanceType === 'Ordinaria' ? 'info' : 'warning'} data-id="element-2208">
                          {item.maintenanceType}
                        </Badge>
                      </td>}
                    {visibleColumns.includes('actions') && <td className="px-4 py-4 pr-6" data-id="element-2209">
                        <div className="flex items-center gap-1" data-id="element-2210">
                          <Tooltip content="Visualizza macchinario" data-id="element-2211">
                            <Button variant="light" size="sm" className="!px-1" onClick={() => handleView(item)} data-id="element-2212">
                              <Eye className="h-4 w-4 text-slate-500" data-id="element-2213" />
                            </Button>
                          </Tooltip>
                          <Tooltip content="Registra manutenzione" data-id="element-2214">
                            <Button variant="light" size="sm" className="!px-1" data-id="element-2215">
                              <Wrench className="h-4 w-4 text-blue-500" data-id="element-2216" />
                            </Button>
                          </Tooltip>
                          <Tooltip content="Modifica macchinario" data-id="element-2217">
                            <Button variant="light" size="sm" className="!px-1" onClick={() => handleEdit(item)} data-id="element-2218">
                              <Edit className="h-4 w-4 text-seform-orange" data-id="element-2219" />
                            </Button>
                          </Tooltip>
                          <Tooltip content="Elimina macchinario" data-id="element-2220">
                            <Button variant="light" size="sm" className="!px-1" onClick={() => handleDelete(item)} data-id="element-2221">
                              <Trash2 className="h-4 w-4 text-red-500" data-id="element-2222" />
                            </Button>
                          </Tooltip>
                        </div>
                      </td>}
                  </tr>)}
              </tbody>
            </table>
          </div>
          <div className="border-t border-slate-100" data-id="element-2223">
            <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={goToPage} totalItems={totalItems} pageSize={pageSize} onPageSizeChange={setPageSize} data-id="element-2224" />
          </div>
        </Card>

        <Modal isOpen={isViewOpen} onClose={() => setIsViewOpen(false)} title={selectedItem ? `Dettaglio macchinario — ${selectedItem.code}` : ''} size="lg" data-id="element-2225">
          {selectedItem && <div className="space-y-6" data-id="element-2226">
              <div className="grid grid-cols-2 gap-4" data-id="element-2227">
                <div data-id="element-2228">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2229">
                    Codice
                  </span>
                  <p className="font-mono font-medium mt-1" data-id="element-2230">
                    {selectedItem.code}
                  </p>
                </div>
                <div data-id="element-2231">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2232">
                    Tipologia
                  </span>
                  <p className="font-medium mt-1" data-id="element-2233">{selectedItem.type}</p>
                </div>
                <div className="col-span-full" data-id="element-2234">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2235">
                    Nome
                  </span>
                  <p className="font-medium mt-1" data-id="element-2236">{selectedItem.name}</p>
                </div>
                <div data-id="element-2237">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2238">
                    Responsabile
                  </span>
                  <p className="font-medium mt-1" data-id="element-2239">{selectedItem.responsible}</p>
                </div>
                <div data-id="element-2240">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2241">
                    Tipo manutenzione
                  </span>
                  <div className="mt-1" data-id="element-2242">
                    <Badge variant={selectedItem.maintenanceType === 'Ordinaria' ? 'info' : 'warning'} data-id="element-2243">
                      {selectedItem.maintenanceType}
                    </Badge>
                  </div>
                </div>
                <div data-id="element-2244">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2245">
                    Ultima manutenzione
                  </span>
                  <p className="font-medium mt-1" data-id="element-2246">
                    {selectedItem.lastMaintenance}
                  </p>
                </div>
                <div data-id="element-2247">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2248">
                    Prossima scadenza
                  </span>
                  <p className="font-medium mt-1" data-id="element-2249">
                    {selectedItem.nextMaintenance}
                  </p>
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-2250">
                <Button variant="flat" color="default" onClick={() => setIsViewOpen(false)} data-id="element-2251">
                  Chiudi
                </Button>
                <Button onClick={() => {
              setIsViewOpen(false);
              handleEdit(selectedItem);
            }} startContent={<Edit className="h-4 w-4" data-id="element-2253" />} data-id="element-2252">
                  Modifica
                </Button>
              </div>
            </div>}
        </Modal>

        <Modal isOpen={isEditOpen} onClose={() => setIsEditOpen(false)} title={selectedItem ? 'Modifica macchinario' : 'Nuovo macchinario'} size="lg" data-id="element-2254">
          <AnimatePresence mode="wait" data-id="element-2255">
            {saveSuccess ? <SaveSuccessView label="Macchinario salvato con successo" data-id="element-2256" /> : <motion.div key="form" initial={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} data-id="element-2257">
                <div className="space-y-6" data-id="element-2258">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2259">
                    <Input label="Codice" value={formData.code || ''} onChange={e => setFormData({
                  ...formData,
                  code: e.target.value
                })} data-id="element-2260" />
                    <Select label="Tipologia" value={formData.type || ''} onChange={e => setFormData({
                  ...formData,
                  type: e.target.value
                })} options={[{
                  value: 'Strumento Misura',
                  label: 'Strumento Misura'
                }, {
                  value: 'Hardware',
                  label: 'Hardware'
                }, {
                  value: 'Veicolo',
                  label: 'Veicolo'
                }, {
                  value: 'Attrezzatura',
                  label: 'Attrezzatura'
                }]} required data-id="element-2261" />
                    <Input label="Nome" value={formData.name || ''} onChange={e => setFormData({
                  ...formData,
                  name: e.target.value
                })} required className="col-span-full" data-id="element-2262" />
                    <Input label="Responsabile" value={formData.responsible || ''} onChange={e => setFormData({
                  ...formData,
                  responsible: e.target.value
                })} data-id="element-2263" />
                    <Select label="Tipo manutenzione" value={formData.maintenanceType || ''} onChange={e => setFormData({
                  ...formData,
                  maintenanceType: e.target.value
                })} options={[{
                  value: 'Ordinaria',
                  label: 'Ordinaria'
                }, {
                  value: 'Straordinaria',
                  label: 'Straordinaria'
                }]} data-id="element-2264" />
                    <DatePicker label="Prossima scadenza" value={formData.nextMaintenance || ''} onChange={e => setFormData({
                  ...formData,
                  nextMaintenance: e.target.value
                })} data-id="element-2265" />
                  </div>
                  <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-2266">
                    <Button variant="flat" color="default" onClick={() => setIsEditOpen(false)} data-id="element-2267">
                      Annulla
                    </Button>
                    <Button onClick={handleSave} startContent={<Save className="h-4 w-4" data-id="element-2269" />} data-id="element-2268">
                      Salva macchinario
                    </Button>
                  </div>
                </div>
              </motion.div>}
          </AnimatePresence>
        </Modal>

        <DeleteModal isOpen={isDeleteOpen} onClose={() => setIsDeleteOpen(false)} onConfirm={confirmDelete} itemLabel={selectedItem?.name || ''} deleteSuccess={deleteSuccess} data-id="element-2270" />
      </Layout>;
  }
  // ─── Maintenance Section ──────────────────────────────────────────────────
  if (sectionId === 'maintenance') {
    return <Layout data-id="element-2271">
        {header}
        {toolbar}
        <Card noPadding className="overflow-hidden" data-id="element-2272">
          <div className="overflow-x-auto" data-id="element-2273">
            <table className="w-full text-left border-collapse table-auto" data-id="element-2274">
              <thead data-id="element-2275">
                <tr className="bg-slate-50/50 border-b border-slate-100 text-xs uppercase tracking-wider text-slate-500 font-semibold" data-id="element-2276">
                  {cols.filter(c => visibleColumns.includes(c.key)).map(col => <th key={col.key} className="px-4 py-4" data-id="element-2277">
                        {col.label}
                      </th>)}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-2278">
                {paginatedData.map(item => <tr key={item.id} className="hover:bg-slate-50/80 transition-colors group" data-id="element-2279">
                    {visibleColumns.includes('code') && <td className="px-4 py-4 font-mono text-xs text-slate-500" data-id="element-2280">
                        {item.code}
                      </td>}
                    {visibleColumns.includes('equipment') && <td className="px-4 py-4 text-sm font-medium text-slate-900" data-id="element-2281">
                        {item.equipment}
                      </td>}
                    {visibleColumns.includes('type') && <td className="px-4 py-4" data-id="element-2282">
                        <Badge variant={item.type === 'Ordinaria' ? 'info' : 'warning'} data-id="element-2283">
                          {item.type}
                        </Badge>
                      </td>}
                    {visibleColumns.includes('date') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-2284">
                        {item.date}
                      </td>}
                    {visibleColumns.includes('result') && <td className="px-4 py-4" data-id="element-2285">
                        <Badge variant={item.result === 'Positivo' ? 'success' : 'danger'} data-id="element-2286">
                          {item.result}
                        </Badge>
                      </td>}
                    {visibleColumns.includes('nextDate') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-2287">
                        {item.nextDate}
                      </td>}
                    {visibleColumns.includes('responsible') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-2288">
                        {item.responsible}
                      </td>}
                    {visibleColumns.includes('actions') && <td className="px-4 py-4 pr-6" data-id="element-2289">
                        {actionButtons(item, 'manutenzione')}
                      </td>}
                  </tr>)}
              </tbody>
            </table>
          </div>
          <div className="border-t border-slate-100" data-id="element-2290">
            <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={goToPage} totalItems={totalItems} pageSize={pageSize} onPageSizeChange={setPageSize} data-id="element-2291" />
          </div>
        </Card>

        <Modal isOpen={isViewOpen} onClose={() => setIsViewOpen(false)} title={selectedItem ? `Dettaglio manutenzione — ${selectedItem.code}` : ''} size="lg" data-id="element-2292">
          {selectedItem && <div className="space-y-6" data-id="element-2293">
              <div className="grid grid-cols-2 gap-4" data-id="element-2294">
                <div data-id="element-2295">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2296">
                    Codice
                  </span>
                  <p className="font-mono font-medium mt-1" data-id="element-2297">
                    {selectedItem.code}
                  </p>
                </div>
                <div data-id="element-2298">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2299">
                    Esito
                  </span>
                  <div className="mt-1" data-id="element-2300">
                    <Badge variant={selectedItem.result === 'Positivo' ? 'success' : 'danger'} data-id="element-2301">
                      {selectedItem.result}
                    </Badge>
                  </div>
                </div>
                <div className="col-span-full" data-id="element-2302">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2303">
                    Macchinario
                  </span>
                  <p className="font-medium mt-1" data-id="element-2304">{selectedItem.equipment}</p>
                </div>
                <div data-id="element-2305">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2306">
                    Tipo
                  </span>
                  <div className="mt-1" data-id="element-2307">
                    <Badge variant={selectedItem.type === 'Ordinaria' ? 'info' : 'warning'} data-id="element-2308">
                      {selectedItem.type}
                    </Badge>
                  </div>
                </div>
                <div data-id="element-2309">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2310">
                    Data esecuzione
                  </span>
                  <p className="font-medium mt-1" data-id="element-2311">{selectedItem.date}</p>
                </div>
                <div data-id="element-2312">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2313">
                    Prossima scadenza
                  </span>
                  <p className="font-medium mt-1" data-id="element-2314">{selectedItem.nextDate}</p>
                </div>
                <div data-id="element-2315">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2316">
                    Responsabile
                  </span>
                  <p className="font-medium mt-1" data-id="element-2317">{selectedItem.responsible}</p>
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-2318">
                <Button variant="flat" color="default" onClick={() => setIsViewOpen(false)} data-id="element-2319">
                  Chiudi
                </Button>
                <Button onClick={() => {
              setIsViewOpen(false);
              handleEdit(selectedItem);
            }} startContent={<Edit className="h-4 w-4" data-id="element-2321" />} data-id="element-2320">
                  Modifica
                </Button>
              </div>
            </div>}
        </Modal>

        <Modal isOpen={isEditOpen} onClose={() => setIsEditOpen(false)} title={selectedItem ? 'Modifica manutenzione' : 'Nuova manutenzione'} size="lg" data-id="element-2322">
          <AnimatePresence mode="wait" data-id="element-2323">
            {saveSuccess ? <SaveSuccessView label="Manutenzione salvata con successo" data-id="element-2324" /> : <motion.div key="form" initial={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} data-id="element-2325">
                <div className="space-y-6" data-id="element-2326">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2327">
                    <Input label="Codice" value={formData.code || ''} onChange={e => setFormData({
                  ...formData,
                  code: e.target.value
                })} data-id="element-2328" />
                    <Input label="Macchinario" value={formData.equipment || ''} onChange={e => setFormData({
                  ...formData,
                  equipment: e.target.value
                })} required data-id="element-2329" />
                    <Select label="Tipo" value={formData.type || ''} onChange={e => setFormData({
                  ...formData,
                  type: e.target.value
                })} options={[{
                  value: 'Ordinaria',
                  label: 'Ordinaria'
                }, {
                  value: 'Straordinaria',
                  label: 'Straordinaria'
                }]} required data-id="element-2330" />
                    <DatePicker label="Data esecuzione" value={formData.date || ''} onChange={e => setFormData({
                  ...formData,
                  date: e.target.value
                })} data-id="element-2331" />
                    <Select label="Esito" value={formData.result || ''} onChange={e => setFormData({
                  ...formData,
                  result: e.target.value
                })} options={[{
                  value: 'Positivo',
                  label: 'Positivo'
                }, {
                  value: 'Negativo',
                  label: 'Negativo'
                }]} required data-id="element-2332" />
                    <DatePicker label="Prossima scadenza" value={formData.nextDate || ''} onChange={e => setFormData({
                  ...formData,
                  nextDate: e.target.value
                })} data-id="element-2333" />
                    <Input label="Responsabile" value={formData.responsible || ''} onChange={e => setFormData({
                  ...formData,
                  responsible: e.target.value
                })} className="col-span-full" data-id="element-2334" />
                  </div>
                  <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-2335">
                    <Button variant="flat" color="default" onClick={() => setIsEditOpen(false)} data-id="element-2336">
                      Annulla
                    </Button>
                    <Button onClick={handleSave} startContent={<Save className="h-4 w-4" data-id="element-2338" />} data-id="element-2337">
                      Salva manutenzione
                    </Button>
                  </div>
                </div>
              </motion.div>}
          </AnimatePresence>
        </Modal>

        <DeleteModal isOpen={isDeleteOpen} onClose={() => setIsDeleteOpen(false)} onConfirm={confirmDelete} itemLabel={selectedItem?.code || ''} deleteSuccess={deleteSuccess} data-id="element-2339" />
      </Layout>;
  }
  // Fallback
  return <Layout data-id="element-2340">
      {header}
      <Card data-id="element-2341">
        <p className="text-slate-500 text-center py-8" data-id="element-2342">
          Sezione non disponibile.
        </p>
      </Card>
    </Layout>;
}
// ─── Section Filters Component ────────────────────────────────────────────────
function SectionFilters({
  sectionId,
  filters,
  setFilter
}: {
  sectionId: string;
  filters: any;
  setFilter: (key: string, value: any) => void;
}) {
  switch (sectionId) {
    case 'workers':
      return <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4" data-id="element-2343">
          <Select label="Stato formazione" value={filters.trainingStatus || ''} onChange={e => setFilter('trainingStatus', e.target.value)} options={[{
          value: '',
          label: 'Tutti'
        }, {
          value: 'valid',
          label: 'In regola'
        }, {
          value: 'expiring',
          label: 'In scadenza'
        }, {
          value: 'expired',
          label: 'Scaduto'
        }]} data-id="element-2344" />
          <Select label="Sorveglianza" value={filters.healthStatus || ''} onChange={e => setFilter('healthStatus', e.target.value)} options={[{
          value: '',
          label: 'Tutti'
        }, {
          value: 'fit',
          label: 'Idoneo'
        }, {
          value: 'unfit',
          label: 'Non idoneo'
        }]} data-id="element-2345" />
        </div>;
    case 'deadlines':
      return <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4" data-id="element-2346">
          <Select label="Tipologia" value={filters.type || ''} onChange={e => setFilter('type', e.target.value)} options={[{
          value: '',
          label: 'Tutte'
        }, {
          value: 'Riunione annuale',
          label: 'Riunione annuale'
        }, {
          value: 'Prova di emergenza',
          label: 'Prova di emergenza'
        }, {
          value: 'Tampone alimentare',
          label: 'Tampone alimentare'
        }, {
          value: 'Audit interno',
          label: 'Audit interno'
        }, {
          value: 'Report annuale',
          label: 'Report annuale'
        }]} data-id="element-2347" />
          <Select label="Stato" value={filters.status || ''} onChange={e => setFilter('status', e.target.value)} options={[{
          value: '',
          label: 'Tutti'
        }, {
          value: 'Urgente',
          label: 'Urgente'
        }, {
          value: 'In scadenza',
          label: 'In scadenza'
        }, {
          value: 'Pianificata',
          label: 'Pianificata'
        }, {
          value: 'Completata',
          label: 'Completata'
        }]} data-id="element-2348" />
          <Select label="Responsabile" value={filters.responsible || ''} onChange={e => setFilter('responsible', e.target.value)} options={[{
          value: '',
          label: 'Tutti'
        }, {
          value: 'Alessio',
          label: 'Alessio'
        }, {
          value: 'Lorenzo',
          label: 'Lorenzo'
        }, {
          value: 'Gian Franco',
          label: 'Gian Franco'
        }, {
          value: 'Tiziano',
          label: 'Tiziano'
        }]} data-id="element-2349" />
        </div>;
    case 'contracts':
      return <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4" data-id="element-2350">
          <Select label="Tipo" value={filters.type || ''} onChange={e => setFilter('type', e.target.value)} options={[{
          value: '',
          label: 'Tutti'
        }, {
          value: 'Contratto',
          label: 'Contratto'
        }, {
          value: 'Offerta',
          label: 'Offerta'
        }]} data-id="element-2351" />
          <Select label="Stato" value={filters.status || ''} onChange={e => setFilter('status', e.target.value)} options={[{
          value: '',
          label: 'Tutti'
        }, {
          value: 'Attivo',
          label: 'Attivo'
        }, {
          value: 'In attesa',
          label: 'In attesa'
        }, {
          value: 'Bozza',
          label: 'Bozza'
        }, {
          value: 'Scaduto',
          label: 'Scaduto'
        }]} data-id="element-2352" />
        </div>;
    case 'invoices':
      return <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4" data-id="element-2353">
          <Select label="Stato" value={filters.status || ''} onChange={e => setFilter('status', e.target.value)} options={[{
          value: '',
          label: 'Tutti'
        }, {
          value: 'Pagata',
          label: 'Pagata'
        }, {
          value: 'In attesa',
          label: 'In attesa'
        }, {
          value: 'Bozza',
          label: 'Bozza'
        }, {
          value: 'Scaduta',
          label: 'Scaduta'
        }]} data-id="element-2354" />
        </div>;
    case 'training':
      return <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4" data-id="element-2355">
          <Select label="Tipologia" value={filters.type || ''} onChange={e => setFilter('type', e.target.value)} options={[{
          value: '',
          label: 'Tutte'
        }, {
          value: 'Sicurezza',
          label: 'Sicurezza'
        }, {
          value: 'HACCP',
          label: 'HACCP'
        }, {
          value: 'Privacy',
          label: 'Privacy'
        }]} data-id="element-2356" />
          <Select label="Stato" value={filters.status || ''} onChange={e => setFilter('status', e.target.value)} options={[{
          value: '',
          label: 'Tutti'
        }, {
          value: 'In corso',
          label: 'In corso'
        }, {
          value: 'Programmato',
          label: 'Programmato'
        }, {
          value: 'Completato',
          label: 'Completato'
        }]} data-id="element-2357" />
          <Select label="Modalità" value={filters.mode || ''} onChange={e => setFilter('mode', e.target.value)} options={[{
          value: '',
          label: 'Tutte'
        }, {
          value: 'Sede SEF',
          label: 'Sede SEF'
        }, {
          value: 'Sede cliente',
          label: 'Sede cliente'
        }, {
          value: 'A distanza',
          label: 'A distanza'
        }]} data-id="element-2358" />
        </div>;
    case 'operations':
      return <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4" data-id="element-2359">
          <Select label="Ambito" value={filters.ambito || ''} onChange={e => setFilter('ambito', e.target.value)} options={[{
          value: '',
          label: 'Tutti'
        }, {
          value: 'Sicurezza',
          label: 'Sicurezza'
        }, {
          value: 'Privacy',
          label: 'Privacy'
        }, {
          value: 'HACCP',
          label: 'HACCP'
        }, {
          value: 'Ambiente',
          label: 'Ambiente'
        }]} data-id="element-2360" />
          <Select label="Stato" value={filters.status || ''} onChange={e => setFilter('status', e.target.value)} options={[{
          value: '',
          label: 'Tutti'
        }, {
          value: 'da_pianificare',
          label: 'Da pianificare'
        }, {
          value: 'pianificato',
          label: 'Pianificata'
        }, {
          value: 'calendarizzato',
          label: 'Calendarizzata'
        }, {
          value: 'in_svolgimento_attivo',
          label: 'Attiva'
        }, {
          value: 'in_svolgimento_sospeso',
          label: 'Sospesa'
        }, {
          value: 'completo',
          label: 'Completa'
        }]} data-id="element-2361" />
          <Select label="Operatore" value={filters.operator || ''} onChange={e => setFilter('operator', e.target.value)} options={[{
          value: '',
          label: 'Tutti'
        }, {
          value: 'Alessio',
          label: 'Alessio'
        }, {
          value: 'Lorenzo',
          label: 'Lorenzo'
        }, {
          value: 'Gian Franco',
          label: 'Gian Franco'
        }, {
          value: 'Tiziano',
          label: 'Tiziano'
        }]} data-id="element-2362" />
        </div>;
    case 'non_conformities':
      return <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4" data-id="element-2363">
          <Select label="Tipologia" value={filters.type || ''} onChange={e => setFilter('type', e.target.value)} options={[{
          value: '',
          label: 'Tutte'
        }, {
          value: 'Non conformità maggiore',
          label: 'NC Maggiore'
        }, {
          value: 'Non conformità minore',
          label: 'NC Minore'
        }, {
          value: 'Osservazione',
          label: 'Osservazione'
        }]} data-id="element-2364" />
          <Select label="Origine" value={filters.origin || ''} onChange={e => setFilter('origin', e.target.value)} options={[{
          value: '',
          label: 'Tutte'
        }, {
          value: 'Interna',
          label: 'Interna'
        }, {
          value: 'Cliente (reclamo)',
          label: 'Cliente (reclamo)'
        }, {
          value: 'Fornitore',
          label: 'Fornitore'
        }]} data-id="element-2365" />
          <Select label="Stato" value={filters.status || ''} onChange={e => setFilter('status', e.target.value)} options={[{
          value: '',
          label: 'Tutti'
        }, {
          value: 'Aperta',
          label: 'Aperta'
        }, {
          value: 'Sospesa',
          label: 'Sospesa'
        }, {
          value: 'Chiusa',
          label: 'Chiusa'
        }]} data-id="element-2366" />
        </div>;
    case 'improvements':
      return <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4" data-id="element-2367">
          <Select label="Stato" value={filters.status || ''} onChange={e => setFilter('status', e.target.value)} options={[{
          value: '',
          label: 'Tutti'
        }, {
          value: 'Approvato',
          label: 'Approvato'
        }, {
          value: 'In valutazione',
          label: 'In valutazione'
        }, {
          value: 'Sospesa',
          label: 'Sospesa'
        }, {
          value: 'Scartata',
          label: 'Scartata'
        }]} data-id="element-2368" />
          <Select label="Responsabile" value={filters.responsible || ''} onChange={e => setFilter('responsible', e.target.value)} options={[{
          value: '',
          label: 'Tutti'
        }, {
          value: 'Alessio',
          label: 'Alessio'
        }, {
          value: 'Lorenzo',
          label: 'Lorenzo'
        }, {
          value: 'Gian Franco',
          label: 'Gian Franco'
        }, {
          value: 'Tiziano',
          label: 'Tiziano'
        }]} data-id="element-2369" />
        </div>;
    case 'equipment':
      return <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4" data-id="element-2370">
          <Select label="Tipologia" value={filters.type || ''} onChange={e => setFilter('type', e.target.value)} options={[{
          value: '',
          label: 'Tutte'
        }, {
          value: 'Strumento Misura',
          label: 'Strumento Misura'
        }, {
          value: 'Hardware',
          label: 'Hardware'
        }, {
          value: 'Veicolo',
          label: 'Veicolo'
        }, {
          value: 'Attrezzatura',
          label: 'Attrezzatura'
        }]} data-id="element-2371" />
          <Select label="Tipo manutenzione" value={filters.maintenanceType || ''} onChange={e => setFilter('maintenanceType', e.target.value)} options={[{
          value: '',
          label: 'Tutti'
        }, {
          value: 'Ordinaria',
          label: 'Ordinaria'
        }, {
          value: 'Straordinaria',
          label: 'Straordinaria'
        }]} data-id="element-2372" />
        </div>;
    case 'maintenance':
      return <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4" data-id="element-2373">
          <Select label="Tipo" value={filters.type || ''} onChange={e => setFilter('type', e.target.value)} options={[{
          value: '',
          label: 'Tutti'
        }, {
          value: 'Ordinaria',
          label: 'Ordinaria'
        }, {
          value: 'Straordinaria',
          label: 'Straordinaria'
        }]} data-id="element-2374" />
          <Select label="Esito" value={filters.result || ''} onChange={e => setFilter('result', e.target.value)} options={[{
          value: '',
          label: 'Tutti'
        }, {
          value: 'Positivo',
          label: 'Positivo'
        }, {
          value: 'Negativo',
          label: 'Negativo'
        }]} data-id="element-2375" />
        </div>;
    default:
      return null;
  }
}